/**
 * Root redux-saga orchestration for the mediaDetails state module (the open media file).
 *
 * Bootstraps the media details page: loads the TDO, detection / tracking / transcription /
 * speaker-separation engine results and settings profiles, then coordinates the worker-hosted
 * store through the IN_* / OUT_* actions from src/common-app/web-worker. A run still going
 * when the file is opened is picked up by `lookupRunningJobs`, which polls its OUTPUT WRITER
 * — the task whose completion means the results exist — for every audio engine, separation
 * included (VE-30784). Also handles save-changes (each save
 * carries the editor's `saveRev`, so one that lands after a mid-save edit leaves the editor
 * dirty — VE-27765), retries,
 * redaction-job and download-asset polling, TDO locking, deleting and downloading redacted
 * assets, status and name changes, and route enter / exit cleanup — with one deliberate
 * exception: an in-flight redaction job's status poll now survives the exit to the folder
 * view, because it is the only thing that reports the finish once the editor is gone
 * (VE-28429). Redact File owns its own
 * save here — `saveBeforeRedact` persists pending redactions before either burn-in path runs,
 * the engine one for audio/video or the browser-side one for images, and stops the run if that
 * save cannot be stored (VE-25870, VE-27738). GraphQL calls live in
 * ../services.ts; handling of worker results is split into ./worker-out.ts.
 */
import { CaseId, TDOId } from '@common-modules/universal/models/Brands';
import { selectUser } from '@helpers/tdoHelper';
// Deep import (not the statusHistory barrel): the barrel pulls the sagas,
// which import this module's actions — a cycle (VE-26730).
import {
  actionFetchLatestStatusRow,
  actionMarkTdoReadVerified,
} from '@common-modules/statusHistory/actions';

import { pickNewestRedactionData } from '@helpers/redactedFileStaleness';
import { isBlankFilename } from '@helpers/draftFilename';
import * as casepointAPI from '@helpers/casepointAPI';
import * as externalIntegrationAPI from '@helpers/externalIntegrationApi';
import * as exterroAPI from '@helpers/exterroAPI';
import * as foiaXpressAPI from '@helpers/foiaXpressAPI';
import * as govQAAPI from '@helpers/govQAAPI';
import * as nuixAPI from '@helpers/nuixAPI';

import moment from 'moment';

import {
  removeTdoLock,
  selectRedactionJob,
  selectRedactionJobCreatedDateTime,
  selectRedactionJobStatus,
  selectTags,
  // selectTdoLockInfo,
  selectTrimInterval,
  workerClearUndoRedoAction,
} from '../index';
// import { actionTryPermission, selectAllowDownload } from '@user-permissions';
import {
  DEFAULT_GLOBAL_SETTINGS,
  REDACT_APP_DEFAULT_PROFILE_SETTINGS,
} from '@helpers/constants';
import {
  actionFetchProfileList,
  FETCH_PROFILE_LIST_FAILURE,
  FETCH_PROFILE_LIST_SUCCESS,
} from '@redact/state/modules/settingsProfile/actions';
import { SettingsProfileType } from '@redact/state/modules/settingsProfile/models';
import {
  selectDefaultProfile,
  selectProfileList,
  selectStore as selectSettingsProfileStore,
} from '@redact/state/modules/settingsProfile/selectors';
import {
  actionTryPermission,
  selectFeatureFlags,
  selectTrashRetentionDays,
  selectRolePermissions,
} from '@user-permissions';
import { modules } from '@veritone/glc-redux';
import {
  // actionRunDownloadWhenRefresh,
  IN_CLEAR_MEDIA_DATA,
  IN_CLOSE_MEDIA,
  IN_DOWNLOAD_ASSET,
  IN_LOAD_DETECTION_DATA,
  IN_LOAD_MEDIA_DATA,
  IN_LOAD_TRACKING_ENGINE_RESULTS,
  IN_LOAD_TRANSCRIPTION_DATA,
  IN_LOAD_SPEAKER_SEPARATION_DATA,
  IN_LOAD_VOICE_MASK_ASSET,
  IN_REDACT_MEDIA,
  IN_SAVE_CHANGES,
  IN_SET_GLOBAL_SETTINGS,
  IN_SET_TDO,
  OUT_BOUNDING_POLY_COLLECTION,
  OUT_CLUSTER_MAP,
  OUT_DETECTION_CLUSTER_GROUPS,
  // actionOutProgress,
  // OUT_FETCH_DOWNLOAD_ASSET_SUCCESS,
  OUT_FETCH_REDACT_TDO_SUCCESS,
  OUT_POLL_DOWNLOAD_ASSET_FAILED,
  OUT_POLL_DOWNLOAD_ASSET_SUCCESS,
  OUT_SELECTED_BOUNDINGPOLY_GROUP,
  OUT_TEXT_OVERLAY_COLLECTION,
  OUT_UDR_ASSET,
  OUT_UDR_CLUSTER_GROUPS,
  OUT_UDR_COLLECTION,
  POLL_DOWNLOAD_ASSET_STOP,
  SAVE_MEDIA_DATA_SUCCESS,
  SET_SAVE_MEDIA_DATA_MAX_ATTEMPT_FAILURE,
  buildSpeakerSeparationLoadRequest,
} from '@worker';
import { get, isString, maxBy, merge } from 'lodash';
import { redirect } from 'redux-first-router';
import { channel } from 'redux-saga';
import {
  all,
  call,
  cancel,
  debounce,
  delay,
  fork,
  put,
  putResolve,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
  throttle,
} from 'typed-redux-saga/macro';
import { enqueueSnackbar } from '../../snackbar';
import {
  DetailTDO,
  GlobalSettings,
  UDRsPolyAssetGroupSeriesItem,
} from '../models';

import { actionStopAllEnginePolling } from '../../engines/actions';
import * as OpticalTracking from '../../engines/optical-tracking';
import { selectConfigEngines } from '../../engines/selectors';
import * as Transcription from '../../engines/transcription';
import * as VoiceMask from '../../engines/voice-mask';
import * as SpeakerSeparation from '../../engines/speaker-separation';
// import { selectTranscriptionEngineId } from '../../engines/transcription';
import {
  fetchMediaComments,
  fetchMediaCommentsSuccess,
  fetchMediaCommentsUser,
  fetchMediaCommentsUserFailure,
  fetchMediaUser,
  fetchMediaUserFailure,
  updateComment,
  updateCommentFailure,
  updateCommentSuccess,
} from '@common-modules/mediaDetails/saga/mediaComments';
import { undoSaga } from '@common-modules/mediaDetails/saga/undo';
import {
  ROUTE_HOME,
  ROUTE_LOAD_FAILED,
  ROUTE_MEDIA_DETAILS,
  selectCurrentRoutePayload,
  selectPreviousRoute,
} from '@common-modules/routing';
import { actionSetHasStartedPlayer } from '@common/state/modules/player';
import { selectEnableFolderView } from '@common/user-onboarding/selectors';
import { saveMediaData } from '@common/web-worker/services';
import getLastRedactedFile from '@helpers/getLastRedactedFile';
import { sagaIntl } from '@i18n';
import {
  actionFetchOrgSDOsByTDOsId,
  actionUpsertSDO,
  FETCH_ORGANIZATION_SDOS_BY_ID,
  FETCH_ORGANIZATION_SDOS_BY_ID_FAILURE,
  FETCH_ORGANIZATION_SDOS_BY_ID_SUCCESS,
  UPSERT_ORGANIZATION_SDO,
} from '@redact-modules/mainPage/actions';
import { selectSDOById } from '@redact-modules/mainPage/selectors';
import {
  fetchOrganizationSDOsByTDOsId,
  upsertOrganizationSDO,
} from '@redact-modules/mainPage/services';
import {
  fetchRedactionCodes,
  fetchRedactionCodesFailure,
} from '@redact-modules/redactionCodes/sagas';
import {
  selectHasLoadedRedactionStatuses,
  selectRedactionCustomStatuses,
  selectRedactionStatusSettings,
} from '@redact-modules/redactionStatuses/selectors';
import { watchDeleteFiles } from '@redact-state/modules/casemedia/sagas';
import {
  actionDeleteFile,
  actionUpdateRedactionStatusChange,
  DELETE_FILE_FAILURE,
  DELETE_FILE_RELOCATED,
  DELETE_FILE_SUCCESS,
} from '@redact/state/modules/casemedia/actions';
import { isRedactionStatus } from '@redact/state/modules/casemedia/models';
import { VirtualFolderNames } from '@redact/state/modules/casemedia/store';
import {
  redactFileCompleted,
  redactingFile,
  selectRedactFiles,
} from '../../redactFile';
import type { StatusHistoryNoteOutcome } from '@common-modules/statusHistory/models';
import {
  actionDeleteCaseDrawerSuccess,
  // actionFilterToggleAll,
  actionFilterToggleShowType,
  actionSeekVideo,
  actionSetDeletedOverlaysCache,
  actionSetSelectedGroups,
  CHANGE_UDRS_POLY_ASSET_GROUP_SERIES_ITEM,
  CHANGE_UDRS_POLY_ASSET_GROUP_SERIES_ITEM_SUBMIT,
  CLEAR_PENDING_LOCKED_STATUS,
  CREATE_ROTATE_VIDEO_JOB_FAILURE,
  CREATE_ROTATE_VIDEO_JOB_SUCCESS,
  DELETE_CASE_DRAWER_TDO_REQUEST,
  DELETE_DETECTION_GROUP,
  DELETE_DETECTION_IN_FRAME,
  DELETE_DETECTION_OVERLAY,
  DELETE_DETECTION_SEGMENT,
  // actionSetSelectedGroups,
  DELETE_LAST_ASSET,
  DELETE_UDR_GROUP,
  DELETE_UDR_IN_FRAME,
  DELETE_UDR_OVERLAY,
  DELETE_UDR_SEGMENT,
  DeleteCaseDrawerPayload,
  DISCARD_CHANGES,
  DOWNLOAD_ALL_FILES_SNACKBAR,
  DOWNLOAD_ASSET,
  EXIT_MEDIA_DETAILS,
  FACE_HIGHLIGHT,
  FETCH_DELETE_LAST_ASSET_FAILURE,
  FETCH_DELETE_LAST_ASSET_SUCCESS,
  FETCH_FOLDER_INFO_FAILURE,
  FETCH_FOLDER_INFO_SUCCESS,
  FETCH_LATEST_TDO_AUDIT_LOG_ASSET_FAILURE,
  FETCH_LATEST_TDO_AUDIT_LOG_ASSET_SUCCESS,
  FETCH_STATUS_CHANGE_FAILURE,
  FETCH_STATUS_CHANGE_SUCCESS,
  FETCH_TDO_SUCCESS,
  FETCH_TDO_WITH_FAILURE,
  FETCH_UPDATE_ASSET_ACTION,
  FETCH_UPDATE_SETTINGS_SUCCESS,
  FETCH_WAVEFORM_FAILURE,
  FETCH_WAVEFORM_SUCCESS,
  goToMainPage,
  IN_REDACTION_TAG_KEY,
  INCREMENT_DOWNLOAD_COUNT,
  LOAD_ENGINE_RESULTS_AFTER_DETECT,
  NEW_OVERLAY,
  PAGE_LOADED,
  patchCasepointState,
  patchExternalIntegrationState,
  patchExterroState,
  patchFOIAXpressState,
  patchGovQAState,
  patchNuixState,
  REDACT_TDO,
  REDACTED_FILE_COMPLETED,
  REDACTION_JOB_STATUS_ACTION_STOP_POLLING,
  REDACTION_JOB_STATUS_FAILURE,
  REDACTION_JOB_STATUS_SUCCESS,
  REDIRECT_TO_HOME,
  REFRESH_TDO_PRIMARY_ASSET_ID_SUCCESS,
  RETRY_DOWNLOAD,
  ROTATE_VIDEO,
  saveLocalUDRAssets,
  SELECT_REDACTED_FILES_TAB,
  SEND_REDACTED_FILE_TO_CASEPOINT,
  SEND_REDACTED_FILE_TO_EXTERNAL_INTEGRATION,
  SEND_REDACTED_FILE_TO_EXTERRO,
  SEND_REDACTED_FILE_TO_FOIAXPRESS,
  SEND_REDACTED_FILE_TO_GOVQA,
  SEND_REDACTED_FILE_TO_NUIX,
  SEED_GLOBAL_SETTINGS,
  SET_GLOBAL_SETTINGS,
  SET_PENDING_LOCKED_STATUS,
  SET_TDO_NAME,
  setAudiowaves,
  setGovQASessionId,
  setSelectedUdrGroupOnWebWorkerOnly,
  STATUS_CHANGE,
  TDO_NAME_CHANGE_FAILURE,
  TDO_NAME_CHANGE_SUCCESS,
  UDR_SELECTED_FROM_TIMELINE,
  UPDATE_DOWNLOAD_URL,
  UPDATE_REDACTED_FILE_METRICS,
  UPDATE_START_END_UDRS_LIVE_TRACKING,
  VIDEO_HAS_LOADED,
} from '../actions';
import {
  cleanDeleteOverlayCache,
  DELETE_OVERLAY_CACHE_INTERVAL,
} from '../helpers/cache-helper';
import {
  DEFAULT_REDACTION_STATUS_LABEL,
  findCustomRedactionStatus,
  getBuiltInRedactionStatusTagWriteFields,
  getRedactionStatusChangeFieldsFromIdentity,
  getRedactionStatusDisplayLabel,
  getRedactionStatusIdentity,
  isRedactionStatusFilter,
  isRedactionStatusEnabled,
  isRedactionStatusLockEdits,
  isWellKnownRedactionStatusToDo,
  normalizeRedactionStatusFilter,
  REDACTION_STATUS_DRAFT,
  REDACTION_STATUS_LABEL_DRAFT,
  type RedactionStatusTagWriteFields,
  resolveRedactionStatusDisplayLabel,
  resolveRedactionStatusKey,
} from '../redactionStatuses';
import {
  getLastRedactExport,
  isFaceDetectionReadySelector,
  isRedactionMediaDataReadySelector,
  local,
  pickLastCompletedTaskByCompletedDate,
  pickLastCompletedTaskByCreatedDate,
  selectAllDownloadTask,
  selectAllRedactTask,
  selectBackgroundBlurEdits,
  selectCurrentTdoId,
  selectDeletedOverlaysCache,
  selectDetectObjects,
  selectFilterParameters,
  selectGlobalSettings,
  selectTdoSettingsFromServer,
  selectGovQASessionId,
  selectIsLoaded,
  selectIsSaveRunning,
  // selectMediaDetailsPage,
  selectIsTdoExternallyModified,
  selectIsTdoReady,
  selectMediaDuration,
  selectOriginalFileStatus,
  selectPendingLockedStatus,
  selectPendingLockedStatusNote,
  selectPendingLockedStatusResponse,
  selectPendingLockedStatusResponseType,
  selectRedactionRunning,
  selectSelectedUDRGroupId,
  selectTdo,
  selectTdoIsChanged,
  selectTrackingRunning,
  selectTranscriptAssets,
  selectSpeakerSeparationPollTask,
  selectTranscriptionRunning,
  selectTranscriptionView,
  selectUdrAsset,
  selectVoiceMaskRunning,
  selectWordsRedactedMap,
} from '../selectors';
import {
  deleteAllHeadDetectionResults,
  deleteLastAsset,
  fetchCaseMedia as fetchCaseMediaService,
  fetchFolderInfo as fetchFolderInfoService,
  fetchLatestTDOAuditLogAsset,
  fetchRedactedMedia,
  fetchRedactionJobStatus,
  fetchTdo,
  fetchWaveformCsv,
  queryTdoModified,
  refreshTdoPrimaryAsset,
  refreshTdoTasks,
  rotateVideo,
  markRedactionDataBurnedIn,
  updateLastRedactedFileMetrics,
  updateNameOnDetails,
  updateRedactionStatusOnDetails,
  settingsAsUserChange,
  updateSettingsOnDetails,
  updateStatusOnDetails,
  updateTDOStatus,
} from '../services';
import { auditLogsSaga } from './audit-logs';
import {
  fetchCaseMediaFailure,
  fetchCaseMedia as fetchCaseMediaSaga,
} from './fetchCaseMedia';
import { isImageFile, redactImageFile } from './imageRedactionSaga';
import { keyboardSaga } from './keyboard';
import { watchDeleteTranscriptionResults } from './transcription-delete.saga';
import { watchDeleteSpeakerSeparationResults } from './speaker-separation-delete.saga';
import { videoSaga } from './video';
import {
  viewSaga,
  watchCheckTdoPermissions,
  watchFindTdoLocks,
  watchRemoveStoredTdoLocks,
  watchRemoveTdoLock,
  watchTdoLock,
  watchTdoLockFound,
  watchTdoLockNotFound,
} from './view';
import { workerOutSaga } from './worker-out';

const DELETE_RELOCATION_TIMEOUT_MS = 15000;

const {
  user: { fetchUser },
} = modules;

export function* initMediaDetails() {
  yield* all([
    fork(toggleAutoSave),
    fork(watchSaveChanges),
    fork(onActionStatusChangeSuccess),
    fork(onActionFetchOrgSDOsByTDOsId),
    fork(onActionUpsertOrgSDO),
    fork(watchDeleteAsset),

    fork(watchSendDownloadRequestHandler),
    fork(downloadAssetToWorker),
    fork(watchDownloadFile),
    fork(watchDownloadFileFailed),
    fork(watchRetryDownloadChannel),
    fork(watchRetryDownload),
    fork(watchDownloadAllRedactedFile),

    fork(watchUpdateLastRedactedFileMetrics),
    fork(watchIncrementDownloadCount),
    // fork(watchStayOnMediaDetails),
    fork(watchGoToHomePage),
    fork(watchGoToMediaDetailsPage),
    fork(watchRedactTDOToWorker),
    fork(watchPollRedactionJob),
    fork(watchRetrieveRedactionJobResults),
    // fork(watchTdoTaskChanges),
    fork(watchUdrTrackingStart),
    fork(watchUdrTrackingSuccess),
    fork(watchTranscriptionSuccess),
    fork(watchSpeakerSeparationSuccess),
    fork(watchVoiceMaskSuccess),
    fork(watchDeleteTranscriptionResults),
    fork(watchDeleteSpeakerSeparationResults),
    fork(loadSettingsFromDetails),
    fork(watchForSetGlobalSettings),
    fork(watchForSeedGlobalSettings),
    fork(watchForChangeName),
    fork(watchForStatusChange),
    fork(watchSaveSuccessForPendingLock),
    fork(watchSaveFailureForPendingLock),
    fork(watchSendToReloadResults),
    // fork(onActionNewOverlay),
    fork(auditLogsSaga),
    fork(viewSaga),
    fork(keyboardSaga),
    fork(workerOutSaga),
    fork(videoSaga),
    fork(onChangeUdrsPolyAssetGroupSeriesItem),
    fork(onChangeUdrsPolyAssetGroupSeriesItemSubmit),
    fork(onUpdateStartEndLiveTrackingUdr),
    fork(onUDRSelectedFromTimeline),
    fork(onFetchTDOSuccess),
    fork(onExitMediaDetails),
    fork(onNewOverlay),
    fork(sendRedactedFileToGovQa),
    fork(sendRedactedFileToFOIAXpress),
    fork(sendRedactedFileToExternalIntegration),
    fork(sendRedactedFileToCasepoint),
    fork(sendRedactedFileToExterro),
    fork(sendRedactedFileToNuix),
    fork(watchOverlaySelectionChanges),
    fork(onCleanDeletedOverlaysCache),

    fork(fetchCaseMediaSaga),
    fork(fetchCaseMediaFailure),
    fork(watchRedactedFileCompleted),

    fork(fetchMediaComments),
    fork(fetchMediaCommentsSuccess),
    fork(updateComment),
    fork(updateCommentSuccess),
    fork(updateCommentFailure),
    fork(fetchMediaCommentsUser),
    fork(fetchMediaCommentsUserFailure),
    fork(fetchMediaUser),
    fork(fetchMediaUserFailure),
    fork(watchDeleteCaseDrawerTdo),
    fork(fetchRedactionCodes),
    fork(fetchRedactionCodesFailure),

    fork(watchTdoLock),
    fork(watchTdoLockFound),
    fork(watchTdoLockNotFound),
    fork(watchFindTdoLocks),
    fork(watchRemoveStoredTdoLocks),
    fork(watchRemoveTdoLock),
    fork(watchCheckTdoPermissions),

    fork(watchVideoHasLoaded),

    fork(undoSaga),
    fork(watchRotateVideo),
    fork(watchRotateVideoSuccess),
    fork(watchRotateVideoFailure),
    fork(watchTdoModified),
  ]);
}

export interface Action<P> {
  type: string;
  payload: P;
}

export interface ActionWithMeta<P, M> {
  type: string;
  payload: P;
  meta: M;
}
export function* loadMediaDetailsPage() {
  yield* all([fork(loadRouteData), fork(watchDeleteFiles)]);
}

export function* loadRouteData() {
  yield* all([
    fork(loadTdoData),
    fork(watchForTdoSuccess),
    fork(watchForTdoFailed),
  ]);
}

function* watchDeleteCaseDrawerTdo() {
  yield* all([
    takeEvery(DELETE_CASE_DRAWER_TDO_REQUEST, deleteCaseDrawerTdoRequest),
  ]);
}

/** Leave media details after deleting the file currently open in the editor. */
function* exitAfterDeleteActiveFile(tdoId: DeleteCaseDrawerPayload['tdoId']) {
  yield* put(DISCARD_CHANGES());
  yield* put(IN_CLEAR_MEDIA_DATA());

  const { userId } = yield* select(selectUser);
  const { tdoLock } = yield* select(selectFeatureFlags);
  if (tdoLock && tdoId && userId) {
    yield* put(removeTdoLock(tdoId, userId));
  }

  yield* put(REDIRECT_TO_HOME());
}

export function* deleteCaseDrawerTdoRequest(
  action: Action<DeleteCaseDrawerPayload>
) {
  const { tdoId, fileName, isActiveFile } = action.payload;

  const trashRetentionDays = yield* select(selectTrashRetentionDays);

  yield* put(
    actionDeleteFile({
      tdoId,
      fileName,
      type: VirtualFolderNames.INPUT,
      isSoftDelete: trashRetentionDays > 0,
    })
  );

  const { ok } = yield* race({
    ok: take(
      (a: { type: string; meta?: { variables?: { tdoId?: string } } }) =>
        a.type === DELETE_FILE_SUCCESS.toString() &&
        a.meta?.variables?.tdoId === tdoId
    ),
    err: take(
      (a: { type: string; meta?: { variables?: { tdoId?: string } } }) =>
        a.type === DELETE_FILE_FAILURE.toString() &&
        a.meta?.variables?.tdoId === tdoId
    ),
  });
  if (ok) {
    yield* put(actionDeleteCaseDrawerSuccess({ ...action.payload }));
    if (isActiveFile) {
      // Soft delete: DELETE_FILE_SUCCESS only writes the trash tag; the actual
      // relocation into Trash runs afterwards under this route's saga tree. Let
      // it finish before REDIRECT_TO_HOME tears the tree down, or the move is
      // cancelled and the file is orphaned (tagged but not in Trash). Cap the
      // wait so a cancelled/slow relocation can't freeze the editor. VE-25125.
      if (trashRetentionDays > 0) {
        yield* race({
          relocated: take(
            (a: { type: string; payload?: { tdoId?: string } }) =>
              a.type === DELETE_FILE_RELOCATED.toString() &&
              a.payload?.tdoId === tdoId
          ),
          timeout: delay(DELETE_RELOCATION_TIMEOUT_MS),
        });
      }
      yield* exitAfterDeleteActiveFile(tdoId);
    }
  }
}

function* toggleAutoSave() {
  yield* takeEvery(FETCH_TDO_SUCCESS, toggleAutoSaveHandle);
}

export function* watchRedactedFileCompleted() {
  yield* takeLatest(REDACTED_FILE_COMPLETED, function* () {
    const currentRoutePayload = yield* select(selectCurrentRoutePayload);
    const caseId = currentRoutePayload?.caseId as CaseId;

    if (caseId) {
      yield* put(fetchCaseMediaService({ caseId }));
    }
  });
}

export function getCustomerDefaultSettings(
  defaultProfile?: SettingsProfileType
): GlobalSettings {
  const profile = defaultProfile || REDACT_APP_DEFAULT_PROFILE_SETTINGS;
  const {
    isSaved: _perTdo,
    lastSavedDateTime: _serverOwned,
    ...merged
  } = merge({}, DEFAULT_GLOBAL_SETTINGS, profile.data.settings);

  /**
   * VE-30516: a profile is a settings TEMPLATE and has no business carrying
   * either server-owned field.
   *
   * `parsedSettings` stores whatever the profile form holds, and that form is
   * seeded from `selectGlobalSettings` — which now exposes `isSaved` and
   * `lastSavedDateTime`. So an admin who opens a file and then creates a profile
   * could bake THAT file's "when settings last changed" into an org-wide
   * template, from where it would ride into every file the profile seeds. Older
   * than a burn-in it silently suppresses the stale-output warning; newer it
   * raises one nobody can clear.
   */
  return merged;
}

export function* toggleAutoSaveHandle() {
  /**
   * VE-30516: seed only a TDO the server has no settings for.
   *
   * This used to ask `!settings.isSaved`, a flag that lives inside the settings
   * object and that every writer of that object could destroy — and one did. An
   * already-configured file then looked unseeded, and seeding REPLACES settings
   * wholesale with the org default profile, so the user's blur configuration was
   * silently reset to the org default on open. Asking the server-observed fact
   * instead cannot be clobbered by a settings write.
   */
  const settingsFromServer = yield* select(selectTdoSettingsFromServer);
  if (!settingsFromServer) {
    const settingsProfileSlice = yield* select(selectSettingsProfileStore);
    if (settingsProfileSlice) {
      const profileList = yield* select(selectProfileList);
      let userChangedSettings = false;
      if (!profileList?.length) {
        yield* put(actionFetchProfileList({ limit: 1000, offset: 0 }));
        const outcome = yield* race({
          ok: take(FETCH_PROFILE_LIST_SUCCESS),
          err: take(FETCH_PROFILE_LIST_FAILURE),
          // VE-30516: the user saving settings during the wait means there is
          // nothing left to seed — and seeding anyway would overwrite what they
          // just saved, in redux AND on the server, stamp and all. Re-checking a
          // flag afterwards cannot catch this: the SET reducer mirrors the modal
          // payload, whose `isSaved` is still whatever redux held.
          userChanged: take(SET_GLOBAL_SETTINGS),
          timeout: delay(10_000),
        });
        userChangedSettings = !!outcome.userChanged;
      }
      if (!userChangedSettings) {
        const defaultProfile = yield* select(selectDefaultProfile);
        // VE-30516: SEED, not SET - persists the profile without minting a
        // `lastSavedDateTime`. Stamping it here dated a settings "change" to
        // whenever the file happened to be opened, which reads as an edit made
        // after the burn-in.
        yield* put(
          SEED_GLOBAL_SETTINGS(getCustomerDefaultSettings(defaultProfile))
        );
      }
    }
  }

  while (true) {
    const { go } = yield* race({
      go: delay(60_000 * 5), // TODO - move auto save interval to constants
      _1: take(FETCH_TDO_SUCCESS),
      _2: take((a: any) => {
        if (a.type && !isString(a.type)) {
          console.warn('Non-string action type:', a.type.type);
        }
        // TODO: Fix race types so we can use UnknownAction here
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
        return a.type?.startsWith?.('route/');
      }),
    });
    if (go) {
      yield* put(FETCH_UPDATE_ASSET_ACTION());
    } else {
      break;
    }
  }
}

/**
 * Load TDO async cycle.
 */

export const loadTdoData = function* () {
  const tdoId = yield* select(selectCurrentTdoId);
  if (tdoId) {
    yield* put(fetchTdo(tdoId));
    // VE-26730: the tier-1 status-history read is NOT dispatched here — it hangs
    // off FETCH_TDO_SUCCESS instead (watchForTdoSuccessHandle), so we never ask
    // for a file's history until this TDO has actually loaded for this caller.
  }
};

export const watchForTdoSuccess = function* () {
  yield* takeEvery(FETCH_TDO_SUCCESS, watchForTdoSuccessHandle);
};

export const watchForTdoSuccessHandle = function* ({
  payload: { temporalDataObject: tdo },
}: {
  payload: { temporalDataObject: DetailTDO };
}) {
  const tdoId = tdo.id;

  // VE-26730: tier-1 status-history read — the newest row drives the
  // correction-note pop-up/icon. Best-effort and gate-aware.
  //
  // Triggered by the TDO having LOADED, not by route entry: history rows carry
  // user-authored correction notes and the acting user's email, and the SDO read
  // is authorized at schema level rather than per TDO, so a tdoId taken straight
  // off the URL must not be enough to reach them. FETCH_TDO_SUCCESS is a
  // server-acknowledged read of this TDO for this caller, so it both verifies
  // the TDO for the statusHistory read funnel (SECURITY-11 — the read sagas
  // drop fetches for unverified TDOs) and triggers the tier-1 read.
  if (tdoId) {
    yield* put(actionMarkTdoReadVerified({ tdoId }));
    yield* put(actionFetchLatestStatusRow({ tdoId }));
  }
  const {
    opticalTrackingEngineId,
    transcriptionCategoryId,
    detectionEngineIds,
    detectionCategory,
    transcriptionEngineId,
    speakerDetectionCategoryId,
    apiRoot,
  } = yield* select(selectConfigEngines);

  const mediaDuration = yield* select(selectMediaDuration);

  const featureFlags = yield* select(selectFeatureFlags);

  // filters through tasks to select [detection engine id, object tracking id, transcription engine id]
  const hasTranscriptionEngine = (tdo?.tasks?.records || [])?.find(
    (r) => r?.engine.categoryId === transcriptionCategoryId
  );
  /**
   * Whether to fetch speaker-separation results for this media.
   *
   * NOT simply the feature flag. The flag is a gate on STARTING the engine —
   * turning it off must stop new jobs without hiding work already paid for,
   * which is what the flag's own contract says (see user-permissions/selectors)
   * and what the delete option depends on: it is reachable only while a
   * separated transcript is loaded, so a flag-only gate would strand results
   * with no way to view or remove them.
   *
   * Any task in the category means this media may already have results, so ask
   * for them regardless. A TDO that never ran separation and has the flag off —
   * the common case — still issues no query, which is what review B6 was about.
   */
  const hasSpeakerSeparationTask = (tdo?.tasks?.records || []).some(
    (r) =>
      !!speakerDetectionCategoryId &&
      r?.engine.categoryId === speakerDetectionCategoryId
  );
  const engines = (tdo?.tasks?.records || [])
    // TODO: veritone-types exports TaskStatus from a pure definition file which is invalid - should replace veritone-types
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    .filter(({ status }) => status === 'complete')
    .reduce<[string | null, string, string | null]>(
      ([id0, id1, id2], { engineId, engine }) => [
        engine.categoryId === detectionCategory ||
        detectionEngineIds.includes(engineId) // for GPU version of engine we are going start using object detection category instead of facialRecognition category so need alternate matching by engine Id as well
          ? engineId
          : id0,
        id1,
        engine.categoryId === transcriptionCategoryId ||
        get(tdo, 'details.veritoneProgram.sourceType', '') === 'trimmed'
          ? engineId
          : id2,
      ],
      [
        null,
        opticalTrackingEngineId,
        get(tdo, 'details.veritoneProgram.sourceType', '') === 'trimmed' ||
        hasTranscriptionEngine
          ? transcriptionEngineId
          : null,
      ]
    );

  if (tdo.waveformAssets) {
    yield* all([
      fork(loadWaveformCsv),
      fork(watchForWaveformSuccess),
      fork(watchForWaveformFailure),
    ]);
  }

  yield* all([
    fork(lookupRunningJobs),
    put(
      IN_LOAD_MEDIA_DATA({
        tdoId,
        engines,
        mediaDuration,
        loadSpeakerSeparation:
          !!featureFlags.enableSpeakerSeparation || hasSpeakerSeparationTask,
      })
    ),
    put(
      IN_SET_TDO({
        apiRoot: apiRoot,
        featureFlags,
        tdo: {
          id: tdo.id,
          startDateTime: tdo.startDateTime,
          details: tdo.details,
        },
      })
    ),
  ]);

  const isPageLoaded = yield* select(selectIsLoaded);
  if (!isPageLoaded) {
    yield* put(PAGE_LOADED());
  }
};

export const watchForTdoFailed = function* () {
  yield* takeEvery(FETCH_TDO_WITH_FAILURE, function* () {
    yield* put(ROUTE_LOAD_FAILED());
  });
};

/**
 * Check for running jobs. Start polling, if found.
 */
export function* lookupRunningJobs() {
  // Pull all running job tasks.
  const tdoId = yield* select(selectCurrentTdoId);
  // const detectOn = yield select(selectDetectionRunning); // TODO - why was this removed?
  const trackingOn = yield* select(selectTrackingRunning);
  const transcriptionOn = yield* select(selectTranscriptionRunning);
  const voiceMaskOn = yield* select(selectVoiceMaskRunning);
  const redactOn = yield* select(selectRedactionRunning);
  // Already narrowed to one task, preferring the output writer — see
  // selectSpeakerSeparationPollTask (VE-30784).
  const separationOn = yield* select(selectSpeakerSeparationPollTask);
  // const downloadOn = yield select(selectDownloadRunning); // We don't use this engine anymore

  // TODO we should enforce only polling for 1 task per engine to prevent double polling

  // TODO: type this better
  const startPollingActions = tdoId
    ? [
        // ...detectOn.filter(oldJobs).map(({ jobId: id }) => [
        //   put,
        //   {
        //     type: FACES_JOB_STATUS_ACTION_START_POLLING,
        //     payload: { id },
        //   },
        // ]),
        ...trackingOn.map(
          ({ jobId }) =>
            [
              put,
              OpticalTracking.startPollingEngineResultsAction({ tdoId, jobId }),
            ] as const
        ),
        ...transcriptionOn.map(
          ({ id: taskId, jobId }) =>
            [
              put,
              Transcription.startPollingEngineResultsAction({
                tdoId,
                taskId,
                jobId,
              }),
            ] as const
        ),
        ...voiceMaskOn.map(
          ({ id: taskId, jobId }) =>
            [
              put,
              VoiceMask.startPollingEngineResultsAction({
                tdoId,
                taskId,
                jobId,
              }),
            ] as const
        ),
        // Separation had no poller at all until VE-30784, so a run started by
        // the upload DAG never loaded its results in-session — the duck only
        // watches jobs it created itself, and the DAG's is the ingestion job.
        ...(separationOn
          ? [
              [
                put,
                SpeakerSeparation.startPollingEngineResultsAction({
                  tdoId,
                  taskId: separationOn.id,
                  jobId: separationOn.jobId,
                }),
              ] as const,
            ]
          : []),
        ...redactOn.map(
          ({ jobId }) =>
            [pollRedactionJobEngineResults, { tdoId, jobId }] as const
        ),
        // ...downloadOn.map(({ jobId }: any) => [
        //   refreshDownload,
        //   { createJob: { targetId: tdoId, id: jobId } },
        // ]),
      ]
    : [];

  for (const [fn, args] of startPollingActions) {
    yield fn(args);
  }
}

// SAVE button
function* watchSaveChanges() {
  yield* takeLatest(FETCH_UPDATE_ASSET_ACTION, watchSaveChangesHandle);
}

export function* watchSaveChangesHandle({
  payload,
}: ReturnType<typeof FETCH_UPDATE_ASSET_ACTION>) {
  const { tdoIsChanged, saveRev } = yield* select(local);

  const tdo = yield* select(selectTdo);
  const isFaceDetectionReady = yield* select(isFaceDetectionReadySelector);
  const isRedactionMediaDataReady = yield* select(
    isRedactionMediaDataReadySelector
  );
  // we now force into view only mode if there is a loading error
  if (
    tdo?.primaryAsset &&
    (tdoIsChanged || payload?.force) &&
    isFaceDetectionReady && // can't save while loading object detection results
    isRedactionMediaDataReady
  ) {
    const tags = yield* select(selectTags);
    const statusTag = tags?.find(
      (tag) => tag?.value === IN_REDACTION_TAG_KEY && !!tag?.redactionStatus
    );
    const currentStatus =
      (statusTag ? getRedactionStatusIdentity(statusTag) : undefined) ||
      DEFAULT_REDACTION_STATUS_LABEL;

    const statusSettings = yield* select(selectRedactionStatusSettings);
    if (
      isWellKnownRedactionStatusToDo(currentStatus) &&
      isRedactionStatusEnabled(REDACTION_STATUS_DRAFT, statusSettings)
    ) {
      yield* put({
        type: STATUS_CHANGE.type,
        payload: { status: REDACTION_STATUS_LABEL_DRAFT },
        // VE-26730: marks the auto-promote as system-initiated so the status
        // history records it with isSystem: true instead of as a user pick.
        meta: { statusHistorySystem: true },
      });
    }

    // always update status so modifiedDateTime on TDO is updated with every save (does this update SDO too?)
    yield* put(updateStatusOnDetails());

    const user = yield* select(selectUser);
    yield* put(
      IN_SAVE_CHANGES({
        tdoId: tdo.id,
        email: user.email,
        // VE-27765: the rev this save covers, so it only calls the editor
        // clean if nothing was edited while it uploaded.
        rev: saveRev,
      })
    );
    // refreshToken using sdk call
    window.aiware?.auth?.reportAppActivity?.();
  } else if (
    !(tdo?.primaryAsset && isFaceDetectionReady && isRedactionMediaDataReady)
  ) {
    // Media isn't ready to save — cancel any pending lock transition
    const pendingStatus = yield* select(selectPendingLockedStatus);
    if (pendingStatus) {
      yield* put(CLEAR_PENDING_LOCKED_STATUS());
      const originalStatus = yield* select(selectOriginalFileStatus);
      yield* put({
        type: STATUS_CHANGE.type,
        payload: { status: originalStatus },
        meta: { isLocalRollback: true },
      });
      const intl = sagaIntl();
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'lockStatusMediaNotReady',
            defaultMessage:
              'Unable to update status: please wait for redactions to finish loading.',
          }),
        })
      );
    }
  }
}

function* onActionFetchOrgSDOsByTDOsId() {
  yield* takeEvery(FETCH_ORGANIZATION_SDOS_BY_ID, function* ({ payload }) {
    yield* put(fetchOrganizationSDOsByTDOsId(payload));
  });
}

/**
 * Watches for TDO status changes. Updates the paired SDO.
 */
export function* onActionStatusChangeSuccess() {
  yield* takeEvery(
    FETCH_STATUS_CHANGE_SUCCESS,
    onActionStatusChangeSuccessHandle
  );
}

export function* onActionStatusChangeSuccessHandle({
  payload,
}: ReturnType<typeof FETCH_STATUS_CHANGE_SUCCESS>) {
  const intl = sagaIntl();
  const tdoId = payload.updateTDO.id;
  const isEnableFolderView = yield* select(selectEnableFolderView);
  if (isEnableFolderView) {
    const statusTag = (payload?.updateTDO?.details?.tags || []).filter(
      (tag) => tag && Object.keys(tag).some((key) => key === 'redactionStatus')
    )?.[0];
    const redactionStatus = statusTag
      ? getRedactionStatusIdentity(statusTag)
      : undefined;
    if (isRedactionStatus(redactionStatus)) {
      yield* put(
        actionUpdateRedactionStatusChange({
          tdoId,
          redactionStatus,
          redactionStatusLabel: statusTag?.redactionStatus,
          ...(statusTag?.redactionStatusKey
            ? { redactionStatusKey: statusTag.redactionStatusKey }
            : {}),
        })
      );
    }
  }
  // if coming from main page sdo should already available
  const sdo = yield* select(selectSDOById(tdoId));
  let sdoId;
  if (sdo) {
    sdoId = sdo.id;
  } else {
    yield* put(actionFetchOrgSDOsByTDOsId({ tdoId: tdoId }));
    const { ok, err } = yield* race({
      ok: take(FETCH_ORGANIZATION_SDOS_BY_ID_SUCCESS),
      err: take(FETCH_ORGANIZATION_SDOS_BY_ID_FAILURE),
    });

    if (ok && ok.payload?.structuredDataObjects?.records?.length) {
      sdoId = ok.payload.structuredDataObjects.records[0]?.id;
    }
    if (err) {
      console.error(
        'onActionStatusChangeSuccessHandle() FETCH_ORGANIZATION_SDOS_BY_ID_FAILURE',
        err
      );
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'errorUpdatingMedia',
            defaultMessage: 'An error occurred while updating media.',
          }),
        })
      );
    }
  }

  if (sdoId) {
    const statusTag = (payload.updateTDO.details?.tags || []).find(
      (tag) => tag?.value === IN_REDACTION_TAG_KEY
    );
    // Identity drives the custom lookup (key when present); the tag's
    // `redactionStatus` is the frozen display name (VE-24546).
    const status =
      (statusTag ? getRedactionStatusIdentity(statusTag) : undefined) ||
      DEFAULT_REDACTION_STATUS_LABEL;
    const statusName = statusTag?.redactionStatus || status;
    const customStatuses = yield* select(selectRedactionCustomStatuses);
    const custom = findCustomRedactionStatus(status, customStatuses);
    const statusKey =
      custom?.statusKey ||
      resolveRedactionStatusKey(status) ||
      normalizeRedactionStatusFilter(status);
    const resolvedLabel = getRedactionStatusDisplayLabel({
      statusKey,
      statusLabel: custom?.statusLabel || statusName,
      isCustomStatus: Boolean(custom),
    });
    yield* put(
      actionUpsertSDO({
        id: sdoId,
        data: {
          tdoId,
          statusLabel: resolvedLabel,
          statusKey,
          isCustomStatus:
            Boolean(custom) || !isRedactionStatusFilter(statusKey),
        },
      })
    );
  }
}

/**
 * Handle Upsert SDO Actions.
 */
function* onActionUpsertOrgSDO() {
  yield* takeEvery(UPSERT_ORGANIZATION_SDO, function* ({ payload }) {
    // !! only allow an update - currently this saga is running even when on the mainPage which otherwise would cause a duplicate sdo to be created!
    if (payload?.id) {
      yield* put(upsertOrganizationSDO(payload));
    }
  });
}

// DELETE button
export function* deleteAsset(action: {
  payload: { id?: string; ids?: string[] };
}) {
  yield* put(deleteLastAsset(action));
  const { ok } = yield* race({
    ok: take(FETCH_DELETE_LAST_ASSET_SUCCESS),
    err: take(FETCH_DELETE_LAST_ASSET_FAILURE),
  });

  const intl = sagaIntl();

  if (ok) {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'latestRedactDeleted',
          defaultMessage: 'Latest redacted file has been successfully deleted',
        }),
      })
    );
    yield* cancel();
  }

  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'latestRedactDeletedFail',
        defaultMessage: 'Latest redacted file deletion failed',
      }),
    })
  );
}

function* watchDeleteAsset() {
  yield* takeLatest(DELETE_LAST_ASSET, deleteAsset);
}

function* watchForSetGlobalSettings() {
  yield* takeLatest(SET_GLOBAL_SETTINGS, watchForSetGlobalSettingsHandle);
}

function* watchForSeedGlobalSettings() {
  yield* takeLatest(SEED_GLOBAL_SETTINGS, watchForSeedGlobalSettingsHandle);
}

/**
 * VE-30516: persist the seeded default profile without claiming the user
 * changed anything.
 *
 * `isSaved` still flips, so the seed happens once per TDO rather than on every
 * load. `lastSavedDateTime` is carried over when the TDO already has one and
 * omitted when it does not - the staleness check treats its absence as "no
 * evidence settings changed after the burn-in", which is exactly the truth here.
 */
export function* watchForSeedGlobalSettingsHandle({ payload }: any) {
  const tdoId = yield* select(selectCurrentTdoId);
  const existing = yield* select(selectGlobalSettings);
  const existingStamp = existing?.lastSavedDateTime;
  if (tdoId) {
    yield* put(
      updateSettingsOnDetails(tdoId, {
        ...payload,
        isSaved: true,
        ...(existingStamp ? { lastSavedDateTime: existingStamp } : {}),
      })
    );
  }
}

export function* watchForSetGlobalSettingsHandle({ payload }: any) {
  const tdoId = yield* select(selectCurrentTdoId);
  if (tdoId) {
    // VE-25870: the stamp is the more useful form of `isSaved` — settings that
    // changed after a burn-in mean the output no longer reflects them.
    yield* put(updateSettingsOnDetails(tdoId, settingsAsUserChange(payload)));
  }
}

function* watchForChangeName() {
  yield* takeLatest(SET_TDO_NAME, watchForChangeNameHandle);
}

export function* watchForChangeNameHandle({ payload }: { payload: string }) {
  // The top bar refuses blank names too; refused here as well so no other
  // caller can store an extension-only name (VE-32151).
  if (payload && !isBlankFilename(payload)) {
    yield* put(updateNameOnDetails(payload));
    const { ok } = yield* race({
      ok: take(TDO_NAME_CHANGE_SUCCESS),
      err: take(TDO_NAME_CHANGE_FAILURE),
    });

    const intl = sagaIntl();

    if (ok) {
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'editNameTDOSuccess',
            defaultMessage: 'TDO name updated successfully.',
          }),
        })
      );
      yield* cancel();
    }

    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'editNameTDOFailed',
          defaultMessage: 'Could not rename the file.',
        }),
        variant: 'error',
      })
    );
  }
}
function* watchForStatusChange() {
  yield* takeLatest(STATUS_CHANGE, watchForStatusChangeHandle);
}

export function* watchForStatusChangeHandle({
  payload,
  meta,
}: ReturnType<typeof STATUS_CHANGE> & {
  // statusHistorySystem / statusHistoryNote ride along for the statusHistory
  // watcher (VE-26730); this handler only acts on isLocalRollback.
  meta?: {
    isLocalRollback?: boolean;
    statusHistorySystem?: boolean;
    statusHistoryNote?: string;
    statusHistoryResponse?: string;
    statusHistoryResponseType?: StatusHistoryNoteOutcome;
  };
}) {
  if (meta?.isLocalRollback) {
    return;
  }

  const status = payload.status || DEFAULT_REDACTION_STATUS_LABEL;
  // Fields persisted on the tag: the picker supplies the human name + custom
  // UUID; a programmatic dispatch (auto-promote, pending-locked replay) sends
  // only the identity, so derive a built-in label from it (VE-24546).
  const statusWriteFields: RedactionStatusTagWriteFields = payload.statusLabel
    ? {
        redactionStatus: payload.statusLabel,
        ...(payload.statusKey ? { redactionStatusKey: payload.statusKey } : {}),
      }
    : getBuiltInRedactionStatusTagWriteFields(status);
  const statusSettings = yield* select(selectRedactionStatusSettings);
  const customStatuses = yield* select(selectRedactionCustomStatuses);
  const statusesLoaded = yield* select(selectHasLoadedRedactionStatuses);
  const nextStatusIsLocked = isRedactionStatusLockEdits(
    status,
    statusSettings,
    customStatuses,
    statusesLoaded
  );
  const currentStatus = yield* select(selectOriginalFileStatus);
  function* rollbackStatusLocally() {
    yield* put({
      type: STATUS_CHANGE.type,
      // Carry the name + custom key so the optimistic tag written by the
      // reducer restores the exact prior status, not a derived built-in label.
      payload: currentStatus
        ? getRedactionStatusChangeFieldsFromIdentity(
            currentStatus,
            customStatuses
          )
        : { status: currentStatus },
      meta: { isLocalRollback: true },
    });
  }

  if (nextStatusIsLocked) {
    const hasTDOChanged = yield* select(selectTdoIsChanged);
    const isSaveRunning = yield* select(selectIsSaveRunning);
    if (hasTDOChanged || isSaveRunning) {
      // VE-26730: park the note with the status. The replay below is what
      // actually persists this transition, so a note left behind here would
      // vanish with no error — the status would land without its note.
      yield* put(
        SET_PENDING_LOCKED_STATUS({
          status,
          ...(meta?.statusHistoryNote ? { note: meta.statusHistoryNote } : {}),
          ...(meta?.statusHistoryResponse
            ? { response: meta.statusHistoryResponse }
            : {}),
          ...(meta?.statusHistoryResponseType
            ? { responseType: meta.statusHistoryResponseType }
            : {}),
        })
      );
      if (!isSaveRunning) {
        yield* put(FETCH_UPDATE_ASSET_ACTION());
      }
      return;
    }
  }

  const pendingLockedStatus = yield* select(selectPendingLockedStatus);
  if (!nextStatusIsLocked && pendingLockedStatus) {
    yield* put(CLEAR_PENDING_LOCKED_STATUS());
  }

  yield* put(updateRedactionStatusOnDetails(statusWriteFields));

  const tdo = yield* select(selectTdo);
  if (!tdo?.id) {
    yield* rollbackStatusLocally();
    return;
  }
  const { ok } = yield* race({
    ok: take(
      (action: any) =>
        action.type === FETCH_STATUS_CHANGE_SUCCESS.type &&
        action.payload?.updateTDO?.id === tdo.id
    ),
    err: take(
      (action: any) =>
        action.type === FETCH_STATUS_CHANGE_FAILURE.type &&
        action.meta?.variables?.tdoId === tdo.id
    ),
  });

  const intl = sagaIntl();
  if (ok) {
    if (nextStatusIsLocked) {
      yield* put(workerClearUndoRedoAction());
    }
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage(
          {
            id: 'statusUpdatedSuccess',
            defaultMessage: 'Status updated to {status} successfully.',
          },
          // Custom statuses store a uuid statusKey on the tag — resolve to the
          // display label so the toast never shows a raw uuid (VE-24546).
          { status: resolveRedactionStatusDisplayLabel(status, customStatuses) }
        ),
      })
    );
    yield* cancel();
  }

  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'statusUpdateFailed',
        defaultMessage: 'Status update failed.',
      }),
    })
  );
  yield* rollbackStatusLocally();
}

export function* onSaveSuccessForPendingLock(
  action: ReturnType<typeof SAVE_MEDIA_DATA_SUCCESS>
) {
  {
    const pendingLockedStatus = yield* select(selectPendingLockedStatus);
    if (!pendingLockedStatus) {
      return;
    }
    const tdo = yield* select(selectTdo);
    const savedContainerId = action.payload?.createAsset?.container?.id;
    if (savedContainerId && tdo?.id && savedContainerId !== tdo.id) {
      return;
    }
    // Read the parked note/reply BEFORE clearing, then clear together.
    const pendingLockedStatusNote = yield* select(
      selectPendingLockedStatusNote
    );
    const pendingLockedStatusResponse = yield* select(
      selectPendingLockedStatusResponse
    );
    const pendingLockedStatusResponseType = yield* select(
      selectPendingLockedStatusResponseType
    );
    yield* put(CLEAR_PENDING_LOCKED_STATUS());
    // Rebuild the human name + custom key from the identity so the replayed
    // write persists both tag fields — a custom locked status would otherwise
    // write its UUID as the display name (VE-24546).
    const customStatuses = yield* select(selectRedactionCustomStatuses);
    yield* put({
      type: STATUS_CHANGE.type,
      payload: getRedactionStatusChangeFieldsFromIdentity(
        pendingLockedStatus,
        customStatuses
      ),
      // VE-26730: the replay is app-initiated (the user's pick was recorded as
      // parked, not persisted) — history flags it isSystem: true. The user's
      // own note rides along so the recorded row keeps it.
      meta: {
        statusHistorySystem: true,
        ...(pendingLockedStatusNote
          ? { statusHistoryNote: pendingLockedStatusNote }
          : {}),
        ...(pendingLockedStatusResponse
          ? { statusHistoryResponse: pendingLockedStatusResponse }
          : {}),
        ...(pendingLockedStatusResponseType
          ? { statusHistoryResponseType: pendingLockedStatusResponseType }
          : {}),
      },
    });
  }
}

function* watchSaveSuccessForPendingLock() {
  yield* takeEvery(SAVE_MEDIA_DATA_SUCCESS, onSaveSuccessForPendingLock);
}

function* watchSaveFailureForPendingLock() {
  yield* takeEvery(SET_SAVE_MEDIA_DATA_MAX_ATTEMPT_FAILURE, function* () {
    const pendingLockedStatus = yield* select(selectPendingLockedStatus);
    if (pendingLockedStatus) {
      yield* put(CLEAR_PENDING_LOCKED_STATUS());
      const currentStatus = yield* select(selectOriginalFileStatus);
      const customStatuses = yield* select(selectRedactionCustomStatuses);
      yield* put({
        type: STATUS_CHANGE.type,
        payload: currentStatus
          ? getRedactionStatusChangeFieldsFromIdentity(
              currentStatus,
              customStatuses
            )
          : { status: currentStatus },
        meta: { isLocalRollback: true },
      });
      const intl = sagaIntl();
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'saveBeforeStatusLockFailed',
            defaultMessage: 'Unable to change status!',
          }),
        })
      );
    }
  });
}

// download
export function* sendDownloadRequestHandler() {
  yield* put(
    actionTryPermission({
      permissionKey: 'allowDownload',
      onAllow: function* () {
        yield* put(UPDATE_REDACTED_FILE_METRICS());
      },
    })
  );
}

export function* watchSendDownloadRequestHandler() {
  yield* takeEvery(DOWNLOAD_ASSET, sendDownloadRequestHandler);
}

export const pickLastAssetByModifiedDate = <
  T extends { modifiedDateTime?: string | number | null },
>(
  assets: T[]
) => {
  if (!assets) {
    return null;
  }

  // if (assets.length === 0) {
  //   return { modifiedDateTime: 0 };
  // }

  return maxBy<T>(assets, (mdt) =>
    new Date(mdt.modifiedDateTime || 0).getTime()
  );
};

export function* watchDownloadFile() {
  yield* takeEvery(OUT_POLL_DOWNLOAD_ASSET_SUCCESS, function* ({ payload }) {
    const exportAssets =
      payload?.payload?.temporalDataObject?.assets?.records || [];

    const hasTDOChanged = yield* select(selectTdoIsChanged);
    if (exportAssets.length > 0) {
      if (hasTDOChanged) {
        yield* put(
          UPDATE_DOWNLOAD_URL({
            downloadUrl: pickLastAssetByModifiedDate(exportAssets)?.signedUri,
          })
        );
      } else {
        const uri = pickLastAssetByModifiedDate(exportAssets)?.signedUri;
        if (uri) {
          window.location.href = uri;
        }
      }
    }
  });
}

export function* watchRetryDownloadChannel() {
  while (true) {
    const action = yield* take(downloadFileChannel);
    yield* put(action);
  }
}

export function* watchRetryDownload() {
  yield* takeEvery(RETRY_DOWNLOAD, retryDownload);
}

export function* retryDownload(action: ReturnType<typeof RETRY_DOWNLOAD>) {
  const { payload } = action;
  const { downloadEngineId, defaultClusterId } =
    yield* select(selectConfigEngines);
  const user = yield* select(selectUser);
  yield* put(
    IN_DOWNLOAD_ASSET({
      tdoId: payload.tdoId,
      tdoName: payload.tdoName,
      downloadEngineID: downloadEngineId,
      clusterId: defaultClusterId,
      email: user.email,
    })
  );
}

export function* watchDownloadAllRedactedFile() {
  yield* takeEvery(DOWNLOAD_ALL_FILES_SNACKBAR, downloadAllSnackbar);
}

export function* downloadAllSnackbar() {
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'downloadAllComplete',
        defaultMessage: 'Download All completed.',
      }),
      variant: 'success',
    })
  );
}

export const downloadFileChannel = channel<ReturnType<typeof RETRY_DOWNLOAD>>();

export function* watchDownloadFileFailed() {
  yield* takeEvery(OUT_POLL_DOWNLOAD_ASSET_FAILED, showSnackbarFailed);
}

export function* showSnackbarFailed(
  action: ReturnType<typeof OUT_POLL_DOWNLOAD_ASSET_FAILED>
) {
  const { payload } = action;

  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'downloadMediaFail',
        defaultMessage: 'Attempt to download media failed',
      }),
      action: 'Retry',
      handleAction: () => downloadFileChannel.put(RETRY_DOWNLOAD(payload)),
    })
  );
}

// update metrics for last redacted asset
export function* watchUpdateLastRedactedFileMetrics() {
  yield* takeLatest(
    UPDATE_REDACTED_FILE_METRICS,
    updateLastRedactedFileMetricsHandler
  );
}

export function* watchIncrementDownloadCount() {
  yield* takeEvery(
    INCREMENT_DOWNLOAD_COUNT,
    updateLastRedactedFileMetricsHandler
  );
}

export function* checkBeforeDownload() {
  const tdo = yield* select(selectTdo);
  if (!tdo) {
    console.log('Error: TDO should have existed');
    return;
  }

  // if export asset contains most recent redacted-media asset will get it here
  const lastExportAsset = getLastRedactExport(tdo);

  let downloadReady = !!lastExportAsset;

  // however if export asset is missing details it must be a legacy asset so must use legacy method to check if up to date
  if (lastExportAsset && !lastExportAsset.details) {
    const downloadTasks = yield* select(selectAllDownloadTask);
    const redactTasks = yield* select(selectAllRedactTask);
    const lastCreatedDownloadTask =
      pickLastCompletedTaskByCreatedDate(downloadTasks);
    const lastCompletedRedactTask =
      pickLastCompletedTaskByCompletedDate(redactTasks);

    downloadReady =
      new Date(lastCreatedDownloadTask?.createdDateTime || 0).getTime() >
      new Date(lastCompletedRedactTask?.completedDateTime || 0).getTime();
  }

  if (downloadReady) {
    // download directly without use engine download
    yield* put(
      POLL_DOWNLOAD_ASSET_STOP({
        jobId: '',
        tdoId: tdo.id,
        status: 'complete',
        tdoName: tdo.name,
      })
    );
  } else {
    // use engine download to create data
    const { downloadEngineId, defaultClusterId } =
      yield* select(selectConfigEngines);
    const user = yield* select(selectUser);
    yield* put(
      IN_DOWNLOAD_ASSET({
        tdoId: tdo.id,
        tdoName: tdo.name,
        downloadEngineID: downloadEngineId,
        clusterId: defaultClusterId,
        email: user.email,
      })
    );
  }
}

// send tdoId, downloadEngineID, email to create jobID
export function* downloadAssetToWorker() {
  yield* takeEvery(DOWNLOAD_ASSET, checkBeforeDownload);
}

export function* updateLastRedactedFileMetricsHandler() {
  yield* put(updateLastRedactedFileMetrics());
}

/** currently removing lock inside MediaDetails/index.tsx instead 
function* watchStayOnMediaDetails() {
  yield* takeEvery(ROUTE_MEDIA_DETAILS, function* ({ payload }) {
  
  const prevRoute = yield* select(selectPreviousRoute);
  // if routed from different media details page need to remove previous lock
  if (prevRoute.type === ROUTE_MEDIA_DETAILS.type && prevRoute.payload?.tdoId) {
    // need make sure not same tdo?
    const { userId } = yield* select(selectUser);
    const { tdoLock } = yield* select(selectFeatureFlags);
    const { isLocked } = yield* select(selectTdoLockInfo);
    // const prevRoute = yield* select(selectPreviousRoute);

    if (prevRoute) {
      const tdoId = prevRoute.payload.tdoId as string | undefined; // maybe there is a better way to extract previous tdo?
      if (tdoLock && tdoId && userId && !isLocked && tdoId != payload.tdoId) {
        yield* put(removeTdoLock(tdoId, userId));
      }
    }
    
  }
}
  );
}
  */

function* watchGoToHomePage() {
  yield* takeLatest([REDIRECT_TO_HOME, ROUTE_HOME], goToHomePageHandle);
}

// Split out so the exit teardown can be driven directly in tests — same
// watcher/handle convention as watchSaveChanges, clusterMapReplace and
// watchUdrTrackingSuccess in this file.
export function* goToHomePageHandle(
  action: ReturnType<typeof REDIRECT_TO_HOME> | { type: string }
) {
  // stop all polls
  if (REDIRECT_TO_HOME.match(action)) {
    const isEnableFolderView = yield* select(selectEnableFolderView);
    if (isEnableFolderView) {
      yield* put(redirect(goToMainPage()));
    } else {
      // window.location.href = '/';
      yield* put(redirect(goToMainPage()));
    }
  } else if (
    get(action, 'meta.location.prev.type') === ROUTE_MEDIA_DETAILS.type
  ) {
    // make sure the lock is removed
    // - if route here using back button will still have lock
    // TODO in some situations the lock already was removed so this could be made more efficient

    // yield* put(removeStoredTdoLocks({})); // this would be simplier but for some reason state.tdoLocks is empty already
    // const tdoLocks = yield* select(selectTdoLocks); // this is returning empty for some reason

    const { userId } = yield* select(selectUser);
    const { tdoLock } = yield* select(selectFeatureFlags);
    // const { isLocked } = yield* select(selectTdoLockInfo);
    const prevRoute = yield* select(selectPreviousRoute);

    if (prevRoute) {
      const tdoId = prevRoute.payload.tdoId as string | undefined; // maybe there is a better way to extract previous tdo?
      if (tdoLock && tdoId && userId) {
        yield* put(removeTdoLock(tdoId, userId));
      }
    }

    // VE-28429: a video/audio redaction runs on the server and keeps going
    // after the user leaves the editor, but its poll is the only thing that
    // reports the finish — the success snackbar, the release of the
    // `redactFile` flag the folder row's "Redacting" indicator reads, and the
    // REDACTED_FILE_COMPLETED that makes casemedia re-read the file's outputs
    // so the row flips to "Redacted". Stopping it here left the row pinned on
    // "Redacting" for the life of the tab, with the finished output reachable
    // only by reloading the folder.
    //
    // Safe to leave running: the poll carries the job's own target (see
    // watchPollRedactionJob) rather than reading the TDO off the route, which is
    // empty here, and it is self-terminating — it stops on any terminal job
    // status and on the 3h-pending / 6h-running stuck guards.
    //
    // The test is the `redactFile` flag for the file being LEFT, not
    // `redactionJob`. That slot looks like the obvious source of truth and is not
    // one: FETCH_REDACTED_MEDIA_SUCCESS nulls it as a side effect, and the editor
    // fetches redacted media while a job is running, so by the time the user
    // leaves it is routinely empty. Asking it tore down live polls — the flag was
    // then released by the poll's own `finally`, silently (no terminal status was
    // ever seen, so no REDACTED_FILE_COMPLETED and no snackbar), and the folder
    // row showed nothing at all for a redaction that was still running.
    //
    // Scoped to this file, not `.some()` over the whole duck: entries are keyed by
    // TDO and never removed, and image and document burn-ins set the same flag
    // without ever starting this poll — so a map-wide test lets one unrelated
    // burn-in suppress the engine-poll teardown for every exit in the session.
    const exitingTdoId = prevRoute?.payload?.tdoId as TDOId | undefined;
    const redactFiles = yield* select(selectRedactFiles);
    const keepRedactionPoll =
      !!exitingTdoId && redactFiles?.[exitingTdoId]?.status === 'running';
    yield* all([
      put(IN_CLOSE_MEDIA()),
      put(IN_CLEAR_MEDIA_DATA()),
      ...(keepRedactionPoll
        ? []
        : [
            put({
              type: REDACTION_JOB_STATUS_ACTION_STOP_POLLING,
              // VE-28429: addressed at the file being left. This stop used to
              // carry no target, and the poll wrapper's race takes it
              // uncorrelated — so leaving ANY editor cancelled a poll that had
              // deliberately survived leaving a different one, dropping that
              // row's "Redacting" without ever reporting completion.
              payload: { noSubActions: true, tdoId: exitingTdoId },
            }),
          ]),
      put(actionStopAllEnginePolling()),
    ]);
  }
}

function* watchGoToMediaDetailsPage() {
  yield* takeLatest(ROUTE_MEDIA_DETAILS, function* () {
    yield* put(IN_CLEAR_MEDIA_DATA());
    yield* put(fetchUser());
  });
}

function* watchUdrTrackingStart() {
  yield* takeLatest(OpticalTracking.PROCESS_ENGINE_REQUEST, function* () {
    yield* put(FETCH_UPDATE_ASSET_ACTION());
  });
}

function* watchUdrTrackingSuccess() {
  yield* takeEvery(
    OpticalTracking.PROCESS_ENGINE_REQUEST_SUCCESS,
    watchUdrTrackingSuccessHandle
  );
}

export function* watchUdrTrackingSuccessHandle({
  payload,
}: ReturnType<typeof OpticalTracking.PROCESS_ENGINE_REQUEST_SUCCESS>) {
  const { tdoId, jobId } = payload;
  const { status } = yield* select(
    OpticalTracking.selectEngineStatus(tdoId, jobId)
  );
  const { opticalTrackingEngineId, detectionCategory } =
    yield* select(selectConfigEngines);
  const mediaDuration = yield* select(selectMediaDuration);
  // DELAY HACK START: Added so engine results have time to become available.
  yield* delay(5_000);
  // DELAY HACK END
  yield* put(
    IN_LOAD_TRACKING_ENGINE_RESULTS({
      ...payload,
      engines: [opticalTrackingEngineId],
      mediaDuration,
      categoryId: detectionCategory,
      // TODO: Is it really valid to pass status here without validating it? Should the selectEngineStatus selector be different?
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      job: { id: jobId, status: status as any },
    })
  );
}

function* watchTranscriptionSuccess() {
  yield* takeLatest(
    Transcription.PROCESS_ENGINE_REQUEST_SUCCESS,
    watchTranscriptionSuccessHandle
  );
}

export function* watchTranscriptionSuccessHandle() {
  const tdoId = yield* select(selectCurrentTdoId);
  // const engineId = yield* select(selectTranscriptionEngineId); // this might not be the engine id that was because can switch transcription engine!
  const mediaDuration = yield* select(selectMediaDuration);
  const { transcriptionCategoryId } = yield* select(selectConfigEngines);
  if (tdoId) {
    yield* put(
      IN_LOAD_TRANSCRIPTION_DATA({
        tdoId,
        // engines: [engineId],
        mediaDuration,
        categoryId: transcriptionCategoryId,
      })
    );
  }
}

/**
 * `takeEvery`, not `takeLatest`: the handler now filters on the run's own
 * tdoId, so a stale success cannot act on the wrong media — and `takeLatest`
 * made that worse rather than better. Pollers can outlive a media switch, so
 * an old TDO's success arriving just after the new one's would cancel the NEW
 * handler during its delay and then bail at the guard, losing the newly opened
 * file's results entirely (VE-30784).
 */
function* watchSpeakerSeparationSuccess() {
  yield* takeEvery(
    SpeakerSeparation.PROCESS_ENGINE_REQUEST_SUCCESS,
    watchSpeakerSeparationSuccessHandle
  );
}

/**
 * A separation run reported complete — load its results.
 *
 * The poller watches the run's OUTPUT WRITER, so by the time this fires the
 * results have been written; the short delay covers indexing. Guarded on the
 * run's own tdoId because `lookupRunningJobs` now starts pollers for runs it
 * finds already going on media open (VE-30784), so a poller can outlive a
 * switch to other media that did not go through the editor's exit — and
 * loading then would query the newly opened file against the old run.
 */
export function* watchSpeakerSeparationSuccessHandle({
  payload,
}: ReturnType<typeof SpeakerSeparation.PROCESS_ENGINE_REQUEST_SUCCESS>) {
  yield* delay(2000);
  const tdoId = yield* select(selectCurrentTdoId);
  if (payload.tdoId !== tdoId) {
    return;
  }
  const mediaDuration = yield* select(selectMediaDuration);
  const { speakerDetectionCategoryId } = yield* select(selectConfigEngines);
  if (tdoId && speakerDetectionCategoryId) {
    yield* put(
      IN_LOAD_SPEAKER_SEPARATION_DATA(
        buildSpeakerSeparationLoadRequest({
          tdoId,
          mediaDuration,
          speakerDetectionCategoryId,
        })
      )
    );
  }
}

function* watchVoiceMaskSuccess() {
  yield* takeLatest(
    VoiceMask.PROCESS_ENGINE_REQUEST_SUCCESS,
    watchVoiceMaskSuccessHandle
  );
}

export function* watchVoiceMaskSuccessHandle() {
  const tdoId = yield* select(selectCurrentTdoId);
  // run query to find the media-voice-conversion asset url
  if (tdoId) {
    yield* put(
      IN_LOAD_VOICE_MASK_ASSET({
        tdoId,
      })
    );
  }
}

/**
 * How long Redact File waits for its save before going ahead without one.
 * Only reached when no save was emitted at all — `watchSaveChangesHandle`
 * declines when the media isn't ready — so it is a floor, not a deadline. Set
 * above the save's own 3 retries at 3s so a genuine failure is reported as a
 * failure rather than timing out into a silent success.
 */
const SAVE_BEFORE_REDACT_TIMEOUT_MS = 20000;

/**
 * Four outcomes, and the difference between the middle two is the whole point.
 * A burn-in label claims a saved asset produced a given output, and only a
 * caller that can tell `clean` from `notSaved` can know whether that is true:
 *
 * - `saved`    — a save was written this run; `savedAssetId` names it. The
 *                strongest answer: the server now holds what the editor holds.
 * - `clean`    — nothing to save. The newest asset already on the TDO is the
 *                current data *as saved* — but see the image branch, which also
 *                has to establish that the editor actually loaded it.
 * - `notSaved` — dirty, and the save never resolved: `watchSaveChangesHandle`
 *                declined it (media not ready) and the timeout floor let the run
 *                continue rather than hang. Nothing on the server describes the
 *                edits the editor is holding.
 * - `failed`   — the save exhausted its retries. Already reported to the user;
 *                the caller's only job is to stop.
 */
interface SaveBeforeRedactOutcome {
  readonly state: 'saved' | 'clean' | 'notSaved' | 'failed';
  /** Id of the asset this save wrote. Only set for `saved`. */
  readonly savedAssetId: string | undefined;
}

/**
 * VE-25870: Redact File saves, then burns in — in that order, and once.
 *
 * Saving here covers the entry points that never saved at all: the keyboard
 * shortcut and the stale banner's own button.
 *
 * A save that cannot be persisted stops the run. Handing a burn-in edits we
 * failed to store would put them in an output while nothing on the server
 * records that it contains them — the file would then be released as if it
 * contained edits no one can account for.
 *
 * Shared by both burn-in paths since VE-27738: the engine one for audio/video
 * and the client-side one for images. Images need the guarantee for a second
 * reason — their burn-in label names the output version this data produced, and
 * only a completed save makes that claim true of anything on the server.
 *
 * Owns its own failure reporting (snackbar plus cleared loading state) so the
 * two callers cannot drift on it. They only have to stop.
 *
 * Reports WHICH of the four outcomes happened rather than a bare pass/fail: the
 * image branch has to treat "clean" and "declined" differently, and collapsing
 * them is how a burn-in comes to label data it never consumed.
 *
 * Waits out a save that was already uploading when this ran before deciding
 * anything — see the first block below for why that is not the same question as
 * "is the editor dirty".
 *
 * Exported for its own tests: the guarantee is worth pinning once here rather
 * than re-walking inside each caller's effect sequence.
 */
export function* saveBeforeRedact(
  tdoId: TDOId
): Generator<any, SaveBeforeRedactOutcome, any> {
  // A save already uploading has to settle before any of this can be answered.
  // `IN_SAVE_CHANGES` clears `tdoIsChanged` the moment a save STARTS, so a run
  // that reads only that flag calls an in-flight save a clean editor — and then
  // labels the asset that save is about to supersede, while the save itself
  // lands unlabelled and reads as never burned in. That is the permanently
  // stale banner this whole scheme exists to prevent, reached by another route.
  //
  // Settling first and re-reading `tdoIsChanged` answers every case on its own:
  // the save lands and nothing else changed (clean, and now naming the asset
  // that actually landed), it lands but the editor was edited during the upload
  // (dirty, so a second save is written), or it exhausted its retries and
  // `tdoIsChanged` is restored (dirty, and the save below reports the failure).
  //
  // Redact File is disabled while a save runs, so this is the backstop for the
  // entry points that do not go through that button.
  if (yield* select(selectIsSaveRunning)) {
    yield* race({
      landed: take(SAVE_MEDIA_DATA_SUCCESS),
      failed: take(SET_SAVE_MEDIA_DATA_MAX_ATTEMPT_FAILURE),
      timedOut: delay(SAVE_BEFORE_REDACT_TIMEOUT_MS),
    });
  }

  // Only wait when a save is actually going to be emitted.
  // `watchSaveChangesHandle` declines on a clean TDO, and rightly so: the
  // newest redaction-data asset already IS the current data, so there is
  // nothing to write. Waiting regardless would stall every Redact File
  // clicked from a saved state by the full timeout — including the stale
  // banner's own button, which fires precisely when the edits are saved but
  // not burned in.
  const { tdoIsChanged } = yield* select(local);
  if (!tdoIsChanged) {
    return { state: 'clean', savedAssetId: undefined };
  }

  yield* put(FETCH_UPDATE_ASSET_ACTION());
  const outcome = yield* race({
    saved: take(SAVE_MEDIA_DATA_SUCCESS),
    failed: take(SET_SAVE_MEDIA_DATA_MAX_ATTEMPT_FAILURE),
    // Dirty but declined anyway — the readiness guard also covers detection
    // results still loading. Nothing will resolve then, so the timeout is the
    // floor that lets the run continue.
    nothingToSave: delay(SAVE_BEFORE_REDACT_TIMEOUT_MS),
  });

  if (outcome.failed) {
    yield* put(redactFileCompleted(tdoId));
    const intl = sagaIntl();
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'redactFileSaveFailed',
          defaultMessage:
            'Could not save your redactions, so the file was not sent for redaction. Please try again.',
        }),
        variant: 'error',
      })
    );
    return { state: 'failed', savedAssetId: undefined };
  }

  // Declined and nothing resolved. Distinguished from `clean` because the
  // editor is dirty: the newest saved asset does NOT describe what it holds.
  if (!outcome.saved) {
    return { state: 'notSaved', savedAssetId: undefined };
  }

  return {
    state: 'saved',
    savedAssetId: outcome.saved.payload?.createAsset?.id,
  };
}

export function* redactTDOToWorker() {
  const tdo = yield* select(selectTdo);

  if (tdo) {
    const isImage = Boolean(yield* call(isImageFile));

    if (isImage) {
      /**
       * VE-27738: the image burn-in runs here in the browser rather than in the
       * blur engine, and owes the staleness check the same label the engine path
       * writes below. Without one the newest save keeps the `burnInRequested:
       * false` that every save carries by default, which reads as "saved but
       * never burned in" — evidence on its own — so the stale banner stays up on
       * every surface that judges the file, and its own REDACT FILE button
       * re-enters this branch and changes nothing.
       *
       * A label claims that a specific saved asset produced a specific output
       * version. It is written only where both halves of that are true:
       *
       * - **An output exists carrying that version.** Hence labelling after a
       *   successful export, with the number the export itself stamped: only one
       *   side derives it, so the two cannot disagree. An export that fails
       *   writes no label at all, which leaves the previous verdict standing —
       *   right whether or not there were unburned edits, where relabelling
       *   first would call an unchanged, already-current file stale.
       * - **The output was built from the saved data.** A save written this run
       *   proves that outright. A clean editor proves it only if the editor
       *   actually holds the saved data: `getAllBoundingPolys` renders whatever
       *   is loaded, and both readiness flags stay false while the detection and
       *   redaction-data bodies load — or for the whole session if either fetch
       *   failed. Exporting from a half-loaded editor produces an under-redacted
       *   file, and that file must keep warning rather than be marked current.
       *
       * The export itself is left alone in that last case: refusing to redact is
       * a product decision beyond this defect, and an under-redacted output that
       * still warns is the behaviour that shipped before this fix.
       */
      // Held for the whole run, not just the export: the save can take seconds,
      // and the eager REDACT FILE flag expires after 10s, so without this the
      // button re-enables mid-run and a second click races this one.
      // `redactImageFile` puts the same action again, which the reducer absorbs.
      yield* put(redactingFile(tdo.id));

      const save = yield* call(saveBeforeRedact, tdo.id);
      if (save.state === 'failed') {
        return;
      }

      // Read before the export, because the export is what consumes the
      // collections these flags describe.
      const detectionsLoaded = yield* select(isFaceDetectionReadySelector);
      const redactionDataLoaded = yield* select(
        isRedactionMediaDataReadySelector
      );
      const builtFromSavedData =
        save.state === 'saved' ||
        (save.state === 'clean' && detectionsLoaded && redactionDataLoaded);

      try {
        const result = yield* call(redactImageFile);

        if (!result.success) {
          // Only reachable with no TDO in state; `redactImageFile` throws on
          // every other failure and is handled below.
          yield* put(redactFileCompleted(tdo.id));
          return;
        }

        if (builtFromSavedData && typeof result.version === 'number') {
          /**
           * Awaited, unlike the engine path's fire-and-forget label.
           *
           * The banner is suppressed only while the burn-in is in flight, and
           * on this path that ends a few lines below — the tab switch and
           * `REDACTED_FILE_COMPLETED` both land in this tick. A label left
           * uploading would resolve after that, so the newly opened tab would
           * read the save as never burned in and flash the stale banner for the
           * length of one round trip. The engine path never shows it because
           * its label is written minutes before the job completes.
           *
           * Still best-effort: a label that fails to write costs precision, not
           * correctness, so it must not take the success snackbar and the tab
           * switch down with it — hence swallowing rather than falling into the
           * export's catch below.
           */
          try {
            yield* putResolve(
              markRedactionDataBurnedIn(
                save.savedAssetId ??
                  pickNewestRedactionData(tdo.redactionDataAssets?.records)?.id,
                result.version
              ) as any
            );
          } catch (_labelError) {
            // Falls back to the timestamp comparison, same as an output burned
            // in before labelling existed.
          }
        }

        // Fire-and-forget: trigger a background refetch so the tab eventually
        // reflects server state. fetchRedactedMedia is a thunk — redux-thunk
        // handles it at runtime, but typed-redux-saga's put expects a plain
        // action, so the cast is required.

        yield* put(fetchRedactedMedia(tdo.id) as any);

        yield* put(SELECT_REDACTED_FILES_TAB({ tdoId: tdo.id }));

        // Success: show snackbar and clear loading state.
        yield* put(REDACTED_FILE_COMPLETED({ tdoId: tdo.id }));
      } catch (_error) {
        // redactImageFile already showed its own error snackbar. Only clear the
        // loading state here — dispatching REDACTED_FILE_COMPLETED would also
        // show the success snackbar, which would be wrong.
        yield* put(redactFileCompleted(tdo.id));
      }
      return;
    }
    // const hasRequestId =
    //   !!tdo.details.govQARequestId ||
    //   !!tdo.details.foiaXpressRequestId ||
    //   !!tdo.details.casepointRequestId;

    const { redactEngineId, defaultClusterId } =
      yield* select(selectConfigEngines);
    const user = yield* select(selectUser);
    const {
      objectTypeEffects,
      audioType,
      audioToneLevel,
      patchPreviousVersion,
    } = yield* select(selectGlobalSettings);

    const { feather: featherEnabled } = yield* select(selectFeatureFlags);

    const trimInterval = yield* select(selectTrimInterval);

    // Refresh primary asset ID because transcoding job may have changed it since last TDO load.
    yield* put(refreshTdoPrimaryAsset(tdo.id));
    const { payload } = yield* take(REFRESH_TDO_PRIMARY_ASSET_ID_SUCCESS);

    if (payload.temporalDataObject?.primaryAsset) {
      /**
       * VE-30516: compute the version BEFORE announcing the run, and announce it
       * once.
       *
       * `assetVersion` is what the engine is told to postfix onto the redacted
       * asset, and the "Processing redacted version N" banner reports it. N used
       * to be derived as `newest output + 1`, which counts OUTPUTS while this
       * counts TASKS — every failed burn-in widens the gap.
       *
       * This used to be computed after `saveBeforeRedact`, with an eager
       * versionless `redactingFile` put first. The banner is already up during
       * that window, so the version selector fell through to the newest
       * selections asset — which still describes the PREVIOUS run — and the
       * banner named a version one behind for as long as the save took, then
       * jumped. `selectAllRedactTask` reads `tdo.tasks`, which the save does not
       * touch, so nothing required the wait.
       */
      const redactTasks = yield* select(selectAllRedactTask);
      const assetVersion = redactTasks ? redactTasks.length + 1 : 1;
      // `startedAt` dates THIS run so the dashboard latch can tell a terminal
      // task left by a previous one from a terminal task belonging to this one;
      // without it, a list fetch landing before the new engine task is
      // queryable releases the flag — and announces success — mid-run.
      yield* put(redactingFile(tdo.id, assetVersion, new Date().toISOString()));

      // Only a hard save failure stops the engine path, as before. `notSaved` is
      // survivable here and not on the image path: the engine reads the redaction
      // data from the server, so the newest saved asset genuinely is what the
      // burn-in consumes, whatever the editor happens to be holding.
      const save = yield* call(saveBeforeRedact, tdo.id);
      if (save.state === 'failed') {
        return;
      }

      const folderId =
        tdo.foldersTreeObjectIds && tdo.foldersTreeObjectIds.length > 0
          ? tdo.foldersTreeObjectIds[0]
          : null;
      const batchId = folderId ? `${folderId}_redaction` : null;

      /**
       * VE-25870: label the redaction data with the output version it is about
       * to produce. `assetVersion` is what the engine is told to postfix onto
       * the redacted asset, so the two sides carry the same number by agreement
       * rather than one having to observe the other — which is what makes the
       * verdict immune to the order these assets happen to be written in.
       *
       * The asset labelled is the one the burn-in consumes: the save just made
       * if there were unsaved edits, otherwise the newest one already on the
       * TDO, which a clean editor guarantees IS the current data. No extra save
       * is written for the clean case.
       *
       * Best-effort. A failure here costs precision, not correctness: the asset
       * keeps no label, and the file falls to the timestamp comparison the same
       * way an output burned in before this scheme does.
       */
      yield* put(
        markRedactionDataBurnedIn(
          save.savedAssetId ??
            pickNewestRedactionData(tdo.redactionDataAssets?.records)?.id,
          assetVersion
        ) as any
      );

      yield* put(
        // TODO Compiler: This payload type is very wrong.
        IN_REDACT_MEDIA({
          tdoId: tdo.id,
          tdoName: tdo.name,
          redactEngineID: redactEngineId,
          clusterId: defaultClusterId,
          batchId,
          primaryAssetId: payload.temporalDataObject.primaryAsset.id,
          email: user.email,
          featherEnabled,
          objectTypeEffects,
          patchPreviousVersion: patchPreviousVersion,
          assetVersion,
          audioType,
          audioToneLevel,
          auditLogs: true, // always create blur-audit-log (used to only happen for external requests)
          ...(trimInterval && {
            trimStartTime: trimInterval.startTimeMs / 1000, // ms -> s
            trimStopTime: trimInterval.stopTimeMs / 1000, // ms -> s
            // trimAssetType: 'redacted-media',
          }),
        })
      );
    }
  }
}

export function* watchRedactTDOToWorker() {
  yield* takeEvery(REDACT_TDO, redactTDOToWorker);
}

// update MDP after Redaction job completes
export function* watchPollRedactionJob() {
  // VE-28429: one poll per JOB, not one poll overall.
  //
  // `takeLatest` here meant starting a redaction on a second file cancelled the
  // first file's poll — and since the poll is the only thing that reports a job
  // finishing, that row never flipped to "Redacted". It did not even go quiet in
  // an obvious way: the flag was released, the row fell back to the engine task,
  // which still said `running` because the job really was running, and it sat on
  // "Redacting" until something else refetched it.
  //
  // Safe to run concurrently now, which it was not when this was written:
  // `loadRedactionJobStatus` correlates on `meta.variables.jobId` and returns
  // that job's own reading (so two polls cannot read each other's results
  // through the shared `redactionJobStatus` slot), `pollRedactionJobEngineResults`
  // refuses a second poll for a job already being polled, and the teardown's
  // STOP_POLLING is addressed at the file it is leaving. Each poll is still
  // self-terminating on a terminal status and on the 3h/6h stuck guards, so the
  // number alive is bounded by the number of redactions actually running.
  yield* takeEvery(OUT_FETCH_REDACT_TDO_SUCCESS, function* () {
    const job = yield* select(selectRedactionJob);
    if (!job) {
      return;
    }
    const tdoId = job.targetId;

    try {
      yield* race({
        // VE-28061: carry the job's own target so completion is reported for
        // the right file even when the user has since opened another editor
        // (see below).
        0: call(pollRedactionJobEngineResults, {
          jobId: job.id,
          tdoId,
        }),
        // VE-28429: only a stop meant for THIS file. An untargeted stop still
        // ends every poll (the teardown paths that mean "stop everything"),
        // but a stop naming another file must not, now that a poll outlives
        // the editor it was started from and two can be alive at once.
        1: take(
          (stop: { type?: string; payload?: { tdoId?: TDOId } }) =>
            stop.type === REDACTION_JOB_STATUS_ACTION_STOP_POLLING &&
            (!stop.payload?.tdoId || stop.payload.tdoId === tdoId)
        ),
      });
    } finally {
      // VE-28061: this poll is the only thing that clears the file's in-memory
      // "Redacting" flag (the `redactFile` duck the folder row, drawer and
      // gallery tile read first). Since VE-28429 it DOES survive leaving the
      // editor, but it can still end early: a redaction started on another file
      // supersedes it (`takeLatest`), and a saga restart cancels it. Each of
      // those used to leave the flag `running` for the life of the tab and the
      // row pinned on "Redacting" long after the engine had finished. Whatever
      // ended the poll, hand the row back to the durable engine-task status by
      // releasing the flag — idempotent when the poll already reported
      // completion. The one exception is a newer job for the SAME file: it lit
      // the flag again before we got here, and it is that job's poll to clear.
      //
      // A poll is no longer cancelled by a redaction on a DIFFERENT file — the
      // watcher above runs one per job — so the only ways in here are a terminal
      // status, a stop addressed at this file, a stuck guard, or a saga restart.
      //
      // The exception below still earns its keep for the SAME file: with one
      // poll per job, starting a new redaction on a file does not end the old
      // job's poll, so that poll reaches this block while the flag belongs to
      // the newer job. Clearing it there would drop "Redacting" on a run that
      // has only just started.
      //
      // Do NOT instead let a completed engine task override a live flag: a file
      // redacted before still shows its OLD completed task while the new job's
      // task has yet to surface, so that reports "done" for a run that just
      // started — the same stale-task trap that made the folder row blank in
      // VE-28429.
      const newer = yield* select(selectRedactionJob);
      const supersededBySameFile =
        Boolean(newer) && newer?.id !== job.id && newer?.targetId === tdoId;
      if (tdoId && !supersededBySameFile) {
        yield* put(redactFileCompleted(tdoId));
      }
    }
  });
}

export interface PollRedactionJobPayload {
  jobId: string;
  /** The job's target TDO; falls back to the route's TDO when absent. */
  tdoId?: TDOId;
}

// `payload` stays untyped at the boundary because lookupRunningJobs invokes this
// through a heterogeneous [fn, args] tuple shared with `put`; it is narrowed on
// the first line.
/**
 * Jobs that already have a poll running.
 *
 * VE-28429: the poll used to be torn down on editor exit, so only one could ever
 * exist. Now that it survives, reopening the file runs `lookupRunningJobs`, which
 * starts a SECOND poll for the same job — two `job(id:)` queries every 5s and, on
 * completion, two REDACTED_FILE_COMPLETED: two success snackbars and two row
 * refreshes for one redaction. Whichever finishes first also puts STOP_POLLING,
 * yanking the other out mid-pass.
 */
const pollingJobIds = new Set<string>();

/**
 * Drop the polled-jobs bookkeeping.
 *
 * Under redux-saga the `finally` below always runs — on completion, on the
 * self-cancel, on takeLatest supersession — so nothing leaks at runtime. A test
 * that steps the generator by hand and abandons it part-way does leak, and would
 * otherwise silently disable the poll for that job id in every later test.
 */
export const resetPolledJobsForTest = () => pollingJobIds.clear();

export const pollRedactionJobEngineResults = function* (payload: any) {
  const { jobId: currentJobId, tdoId: jobTdoId } =
    payload as PollRedactionJobPayload;
  if (pollingJobIds.has(currentJobId)) {
    return;
  }
  pollingJobIds.add(currentJobId);
  try {
    while (true) {
      try {
        const observed = (yield* call(
          loadRedactionJobStatus,
          payload
        )) as unknown as RedactionJobStatusReading | undefined;

        // check response with watermark. VE-28429: prefer the reading this pass
        // just got for THIS job; the store slot is shared by every job and is only
        // a fallback for a timed-out or failed read.
        const slotStatus = yield* select(selectRedactionJobStatus);
        const slotCreatedDateTime = yield* select(
          selectRedactionJobCreatedDateTime
        );
        const jobStatus = observed?.status ?? slotStatus;
        const jobCreatedDateTime =
          observed?.createdDateTime ?? slotCreatedDateTime;

        // VE-28429: no stale-job self-cancel here any more.
        //
        // `selectRedactionJob` is a single slot holding whichever file's job was
        // started last, so this said "a different file is being redacted now —
        // stop watching mine", which is precisely the behaviour one poll per job
        // exists to end. It would have quietly defeated the `takeEvery` above:
        // start a redaction on a second file and the first file's poll killed
        // itself on its next pass, so that row never reported completion.
        //
        // It existed as the de-duplication — supersession was how a second poll
        // for the same job was avoided — and `pollingJobIds` does that job now,
        // per job rather than per app.

        let jobStuck = false;
        if (jobCreatedDateTime) {
          const delayMs = moment().diff(moment(jobCreatedDateTime));

          // if job status is pending for more than 3 hours something probably has gone wrong
          if (delayMs > 10800000 && jobStatus === 'pending') {
            jobStuck = true;
          }

          // if job status is running for more than 6 hours something probably has gone wrong
          if (delayMs > 21600000 && jobStatus === 'running') {
            jobStuck = true;
          }
        }

        if (
          ['complete', 'failed', 'cancelled', 'aborted'].includes(jobStatus) ||
          jobStuck
        ) {
          if (jobStatus === 'complete') {
            // VE-28061: the poll is registered app-wide and keeps running when
            // the user moves straight from this editor into another file's (only
            // Back to the dashboard stops it — see watchPollRedactionJob).
            // Reading the TDO off the route here then reported completion for
            // the WRONG file, so that row's redactFile entry stayed "running" and
            // the success snackbar named the wrong file. Prefer the job's own
            // target; the route is only a fallback for legacy callers.
            let tdoId = jobTdoId;
            if (!tdoId) {
              tdoId = yield* select(selectCurrentTdoId);
            }
            if (tdoId) {
              yield* put(REDACTED_FILE_COMPLETED({ tdoId }));
            }
          }

          yield* put({
            type: REDACTION_JOB_STATUS_ACTION_STOP_POLLING,
            // VE-28429: carry the job's target. The editor-tab refetch this
            // triggers reads whatever TDO happens to be loaded, so it needs to be
            // able to tell that this stop is about the file on screen.
            payload: { jobStatus, tdoId: jobTdoId },
          });
          yield* cancel();
        }
      } catch (err) {
        console.error('Error polling:', err);
      }
      yield* delay(5000);
    }
  } finally {
    // Runs on cancellation too (takeLatest supersession, the self-cancel above,
    // a saga restart), so a job is never left marked as polled with no poll.
    pollingJobIds.delete(currentJobId);
  }
};

/**
 * Fetch this job's status AND wait for the answer.
 *
 * VE-28429: `fetchRedactionJobStatus` is a thunk, so the `put` returns the
 * moment it is dispatched — not when the query lands. The caller
 * (`pollRedactionJobEngineResults`) reads `selectRedactionJobStatus` on its very
 * next line, so without this every pass evaluated the value left by the PREVIOUS
 * pass, and the first pass of a new job evaluated the previous JOB's.
 *
 * It mostly got away with it: the loop re-reads every 5s, so a lagging value
 * only delayed the verdict. The first pass is the dangerous one — a terminal
 * status still sitting in that slot from an earlier job ends the poll
 * immediately, before the new job has reported anything, and the run is then
 * reported against stale data.
 *
 * Bounded, because a lost reply must not park the poll forever: on timeout the
 * caller simply re-reads whatever is there and tries again on the next tick,
 * which is exactly the old behaviour.
 */
export function* loadRedactionJobStatus(payload: { jobId: string }) {
  yield* put(fetchRedactionJobStatus(payload));
  // Correlated on the job, not just the action type. `callGraphQLApi` puts the
  // request `variables` on the reply's meta, which is the only thing that tells
  // two in-flight status reads apart — and two ARE routine now that a poll
  // outlives its editor and reopening the file starts another. Waiting on a bare
  // REDACTION_JOB_STATUS_SUCCESS let one poll be released by the other's reply.
  const matchesThisJob = (action: {
    meta?: { variables?: { jobId?: string } };
  }) => action?.meta?.variables?.jobId === payload.jobId;
  const { ok } = yield* race({
    ok: take(
      (action: { type: string; meta?: { variables?: { jobId?: string } } }) =>
        action.type === REDACTION_JOB_STATUS_SUCCESS.type &&
        matchesThisJob(action)
    ),
    failed: take(
      (action: { type: string; meta?: { variables?: { jobId?: string } } }) =>
        action.type === REDACTION_JOB_STATUS_FAILURE.type &&
        matchesThisJob(action)
    ),
    timeout: delay(REDACTION_JOB_STATUS_TIMEOUT_MS),
  });
  // Hand back THIS job's answer so the caller need not consult the single shared
  // `redactionJobStatus` slot, which any other job's reply overwrites.
  const job = (
    ok as { payload?: { job?: RedactionJobStatusReading } } | undefined
  )?.payload?.job;
  return job;
}

/** One job-status reply, as the poll consumes it. */
export interface RedactionJobStatusReading {
  status?: string;
  createdDateTime?: string;
}

/** Ceiling on one job-status read before the poll gives up and retries. */
const REDACTION_JOB_STATUS_TIMEOUT_MS = 15_000;

export function* watchRetrieveRedactionJobResults() {
  yield* takeLatest(
    REDACTION_JOB_STATUS_ACTION_STOP_POLLING,
    retrieveRedactionJobResults
  );
}

export function* retrieveRedactionJobResults(action: any) {
  if (get(action, 'payload.noSubActions')) {
    return;
  }
  // VE-28429: the poll now outlives the editor, so its terminal STOP_POLLING can
  // land with no document loaded, or with a DIFFERENT one. `fetchRedactedMedia`
  // ignores its argument and reads the id off `mediaDetails.tdo` (which
  // ROUTE_HOME resets to defaultState), so this must MATCH rather than merely
  // check presence: a stop for file A arriving while file B's editor is open
  // would otherwise refetch B's redacted media because A finished — and
  // FETCH_REDACTED_MEDIA_SUCCESS nulls the shared `redactionJob` slot as a side
  // effect, sabotaging B's own in-flight redaction.
  //
  // This refetch only feeds the editor's Redacted File tab; the folder row is
  // brought up to date by REDACTED_FILE_COMPLETED instead. A stop carrying no
  // target is a legacy caller's — fall back to presence, the old behaviour.
  const tdo = yield* select(selectTdo);
  if (!tdo?.id) {
    return;
  }
  const stoppedFor = get(action, 'payload.tdoId') as TDOId | undefined;
  if (stoppedFor && stoppedFor !== tdo.id) {
    return;
  }
  yield* put(fetchRedactedMedia(action));
}

/**
 * UPDATE 3/28/2022 this function is being disabled.
 * The task refresh these action trigger is unnecessary since tasks are already be refreshed continuously in the onFetchTDOSuccessHandle function.
 *
 * Watch for actions that indicate a change in Tasks.
 * Refresh tasks in TDO.
 */
/**
function* watchTdoTaskChanges() {
  const chan: TakeableChannel<Action<unknown>> = yield actionChannel(
    [
      OpticalTracking.CREATE_ENGINE_JOB_SUCCESS,
      OpticalTracking.STOP_POLLING_ENGINE_RESULTS,
      OpticalTracking.CANCEL_JOB_SUCCESS,
      OpticalTracking.PROCESS_ENGINE_REQUEST_SUCCESS,

      Transcription.CREATE_ENGINE_JOB_SUCCESS,
      Transcription.STOP_POLLING_ENGINE_RESULTS,
      Transcription.CANCEL_JOB_SUCCESS,
      Transcription.PROCESS_ENGINE_REQUEST_SUCCESS,

      CREATE_JOB_FACES_SUCCESS,
      // FACES_JOB_STATUS_SUCCESS,
      // FACES_JOB_STATUS_ACTION_STOP_POLLING,

      // POLL_DOWNLOAD_ASSET_SUCCESS,
      // OUT_FETCH_DOWNLOAD_ASSET_SUCCESS,

      OUT_POLL_DOWNLOAD_ASSET_SUCCESS,
      OUT_FETCH_REDACT_TDO_SUCCESS,
      REDACTION_JOB_STATUS_ACTION_STOP_POLLING,
    ],
    buffers.dropping(1)
  );
  yield takeEvery(chan, function* () {
    yield delay(2000); // Let the tasks update

    const tdoId = yield* select(selectCurrentTdoId);
    if (tdoId) {
      yield put(refreshTdoTasks(tdoId, 300));
    }
    yield delay(1000); // Slight debounce
  });
}
*/

function* loadSettingsFromDetails() {
  yield* takeEvery(
    [FETCH_TDO_SUCCESS, FETCH_UPDATE_SETTINGS_SUCCESS],
    loadSettingsFromDetailsHandle
  );
}

export function* loadSettingsFromDetailsHandle() {
  const isReady = yield* select(selectIsTdoReady);
  yield* emptyStatusHandler();
  // If ingestion is not done,
  if (!isReady) {
    const intl = sagaIntl();
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'procNotFinishedWait',
          defaultMessage:
            'This file has not finished processing. Wait for ingestion to complete.',
        }),
      })
    );
    yield* put(goToMainPage());
  } else {
    const globalSettings = yield* select(selectGlobalSettings);
    yield* put(IN_SET_GLOBAL_SETTINGS(globalSettings));
  }
}

// first page open
export const emptyStatusHandler = function* () {
  const tags = yield* select(selectTags);
  if (
    !tags.find(
      (tag) => tag?.value === IN_REDACTION_TAG_KEY && !!tag.redactionStatus
    )
  ) {
    yield* put(updateStatusOnDetails());
  }
};

export function* handleChangeUdrsPolyAssetGroupSeriesItem({
  payload,
}: Action<{
  id: string;
  seriesItem: UDRsPolyAssetGroupSeriesItem;
  changeType: 'start' | 'end' | 'stick';
}>) {
  const { seriesItem, changeType } = payload;

  if (changeType === 'start' || changeType === 'end') {
    yield* put(
      actionSeekVideo({
        startTimeMs:
          changeType === 'start'
            ? seriesItem.startTimeMs
            : seriesItem.stopTimeMs,
      })
    );
  }
}

function* onChangeUdrsPolyAssetGroupSeriesItemSubmit() {
  yield* takeEvery(
    CHANGE_UDRS_POLY_ASSET_GROUP_SERIES_ITEM_SUBMIT,
    function* () {
      const localUDRAsset = yield* select(selectUdrAsset); // selectUdrAsset return UDRsPolyAsset

      yield* put(saveLocalUDRAssets(localUDRAsset));
    }
  );
}

function* onUpdateStartEndLiveTrackingUdr() {
  yield* takeEvery(
    UPDATE_START_END_UDRS_LIVE_TRACKING,
    function* ({ payload: { startPayload, endPayload } }) {
      yield* put(CHANGE_UDRS_POLY_ASSET_GROUP_SERIES_ITEM(startPayload));
      yield* put(CHANGE_UDRS_POLY_ASSET_GROUP_SERIES_ITEM_SUBMIT(startPayload));
      // Must wait until OUT_UDR_ASSET is called meaning UDR has been fully updated
      yield* take(OUT_UDR_ASSET);
      yield* put(CHANGE_UDRS_POLY_ASSET_GROUP_SERIES_ITEM(endPayload));
      yield* put(CHANGE_UDRS_POLY_ASSET_GROUP_SERIES_ITEM_SUBMIT(endPayload));
    }
  );
}

function* onChangeUdrsPolyAssetGroupSeriesItem() {
  yield* throttle(
    300,
    CHANGE_UDRS_POLY_ASSET_GROUP_SERIES_ITEM,
    handleChangeUdrsPolyAssetGroupSeriesItem
  );
}

export function* handleUDRSelectedFromTimeline({
  payload,
}: Action<{
  groupId: string;
  id: string;
  seriesItem: UDRsPolyAssetGroupSeriesItem;
}>) {
  const { seriesItem } = payload;

  yield* put(actionSeekVideo({ startTimeMs: seriesItem.startTimeMs }));
}

function* onUDRSelectedFromTimeline() {
  yield* takeEvery(UDR_SELECTED_FROM_TIMELINE, handleUDRSelectedFromTimeline);
}

/* Removed because selected groups are already set in udrs.ts -> onNewOverlay() */
// function* onActionNewOverlay() {
//   yield* takeEvery(NEW_OVERLAY, function* ({ payload }) {
//     yield* put(actionSetSelectedGroups({ selected: { [payload.id]: true } }));
//   });
// }

function* onFetchTDOSuccess() {
  yield* takeEvery(FETCH_TDO_SUCCESS, onFetchTDOSuccessHandle);
}

function* watchTdoModified() {
  yield* takeEvery(FETCH_TDO_SUCCESS, watchTdoModifedHandle);
}

function* onExitMediaDetails() {
  yield* takeEvery(EXIT_MEDIA_DETAILS, function* () {
    // { payload }) {
    // const { tdoId, userId } = payload;
    // const { isLocked } = yield* select(selectTdoLockInfo);
    // const { tdoLock } = yield* select(selectFeatureFlags);

    const hasTDOChanged = yield* select(selectTdoIsChanged);
    if (hasTDOChanged) {
      yield* put(goToMainPage());
    } else {
      // Lock currently getting removed in ROUTE_HOME instead!
      // if (tdoLock && tdoId && userId && !isLocked) {
      //   yield* put(removeTdoLock(tdoId, userId, "hey"));
      // }

      yield* put(REDIRECT_TO_HOME());
    }
  });
}

export function* onFetchTDOSuccessHandle() {
  // legacy polling ran a full refresh of all tasks every 10 seconds
  // this is overkill and is an expensive query when there are many tasks on a TDO
  // we already poll any running jobs which is more efficient and used to trigger most updates
  // currently this refresh appear to only really be needed to trigger the pop-up task completion notifications

  // to reduce poll load use updated polling pattern
  // every 10 second refresh recent tasks (last 10 tasks)
  // every 1 minute load last 100 tasks in

  // in future we could possibly remove this polling or modulate refresh interval based on whether there are any active tasks

  const REFRESH_INTERVAL = 10_000; // polling interval refresh tasks
  const DEEP_REFRESH_FREQ = 6; // perform deeper task refresh every X number of refreshes
  const SHALLOW_REFRESH_TASK_LIMIT = 10;
  const DEEP_REFRESH_TASK_LIMIT = 100;

  let mod = 0;
  while (true) {
    const { go } = yield* race({
      go: delay(REFRESH_INTERVAL),
      _1: take(ROUTE_HOME),
      _2: take(ROUTE_MEDIA_DETAILS),
    });

    if (go) {
      mod = (mod + 1) % DEEP_REFRESH_FREQ;
      const tdoId = yield* select(selectCurrentTdoId);
      if (tdoId) {
        yield* put(
          refreshTdoTasks(
            tdoId,
            mod === 0 ? DEEP_REFRESH_TASK_LIMIT : SHALLOW_REFRESH_TASK_LIMIT
          )
        );
      }
    } else {
      break;
    }
  }
}

// TODO this poll could probably be combined with other polls
export function* watchTdoModifedHandle() {
  const POLLING_INTERVAL = 30_000;
  while (true) {
    const { go } = yield* race({
      go: delay(POLLING_INTERVAL),
      _1: take(ROUTE_HOME),
      _2: take(ROUTE_MEDIA_DETAILS),
    });
    if (go) {
      const tdoId = yield* select(selectCurrentTdoId);
      const rolePermissions = yield* select(selectRolePermissions);
      const { tdoLock } = yield* select(selectFeatureFlags);

      // const currentPage = yield* select(selectMediaDetailsPage);
      const isTdoExternallyModified = yield* select(
        selectIsTdoExternallyModified
      );

      // if we already know its modified no need to keep checking
      if (isTdoExternallyModified) {
        break;
      }

      // we only need to poll when unable to lock tdo, or feature flag for lock turned off
      if (
        tdoId &&
        (!rolePermissions.lockTdo || !tdoLock)
        // currentPage === SELECT_EDITOR_TAB.type // not sure this check is necessary
      ) {
        yield* put(queryTdoModified(tdoId));
      }
    } else {
      break;
    }
  }
}

export function* onNewOverlay() {
  yield* takeEvery(NEW_OVERLAY, function* () {
    const filterParameters = yield* select(selectFilterParameters);
    if (filterParameters.show.udr) {
      return;
    }

    // TODO if is cbsa all need to toggle all
    // yield* put(actionFilterToggleAll({ filter: true, preventUndo: true }));

    // new - just activate the udr filter no longer is a drop down menu
    yield* put(
      actionFilterToggleShowType({
        filterType: 'udr',
        value: true,
        preventUndo: true,
      })
    );
  });
}

export function* sendRedactedFileToGovQa() {
  const intl = sagaIntl();
  yield* takeEvery(SEND_REDACTED_FILE_TO_GOVQA, function* ({ payload }) {
    yield* put(
      patchGovQAState({
        isSendingFile: true,
      })
    );

    let tdo = yield* select(selectTdo);
    if (!tdo) {
      console.error('TDO should have existed');
      return;
    }
    yield* put(fetchLatestTDOAuditLogAsset(tdo.id));

    const { fetchAuditLogAssetResponse, errFetchingAuditLogAsset } =
      yield* race({
        fetchAuditLogAssetResponse: take(
          FETCH_LATEST_TDO_AUDIT_LOG_ASSET_SUCCESS
        ),
        errFetchingAuditLogAsset: take(
          FETCH_LATEST_TDO_AUDIT_LOG_ASSET_FAILURE
        ),
      });

    if (errFetchingAuditLogAsset) {
      yield* put(
        patchGovQAState({
          successMessage: '',
          isStatusDialogShown: true,
          errorMessage: intl.formatMessage({
            id: 'errorFetchingAuditLog',
            defaultMessage: 'Error fetching audit log',
          }),
        })
      );

      yield* put(
        patchGovQAState({
          isSendingFile: false,
        })
      );

      return;
    }

    const auditLog =
      fetchAuditLogAssetResponse?.payload?.temporalDataObject?.assets
        ?.records?.[0];

    // make sure latest tdo is fetched before sending
    yield* put(fetchTdo(tdo.id));

    const { errFetchingTDO } = yield* race({
      _: take(FETCH_TDO_SUCCESS),
      errFetchingTDO: take(FETCH_TDO_WITH_FAILURE),
    });

    if (errFetchingTDO) {
      yield* put(
        patchGovQAState({
          successMessage: '',
          isStatusDialogShown: true,
          errorMessage: intl.formatMessage({
            id: 'errorFetchingTDOState',
            defaultMessage: 'Error fetching TDO state',
          }),
        })
      );

      yield* put(
        patchGovQAState({
          isSendingFile: false,
        })
      );

      return;
    }

    yield* put(govQAAPI.fetchIntegrationInfo());

    const { ok, err } = yield* race({
      ok: take(govQAAPI.FETCH_INTEGRATION_INFO_SUCCESS),
      err: take(govQAAPI.FETCH_INTEGRATION_INFO_FAILURE),
    });

    if (err) {
      yield* put(
        patchGovQAState({
          successMessage: '',
          isStatusDialogShown: true,
          errorMessage: intl.formatMessage({
            id: 'errorFetchingGovQAIntegrationInfo',
            defaultMessage: 'Error fetching GovQA integration info',
          }),
        })
      );

      yield* put(
        patchGovQAState({
          isSendingFile: false,
        })
      );

      return;
    }

    const {
      BASEURL: baseUrl,
      AUTHKEY: authKey,
      ACTIVATIONKEY: activationKey,
      USERNAME: username,
      PASSWORD: password,
    } = ok?.payload?.me?.organization?.integrationConfig?.config || {};

    if (!baseUrl || !authKey || !activationKey || !username || !password) {
      yield* put(
        patchGovQAState({
          successMessage: '',
          isStatusDialogShown: true,
          errorMessage: intl.formatMessage({
            id: 'missingGovQAIntegrationConfig',
            defaultMessage: 'Missing GovQA integration config',
          }),
        })
      );

      yield* put(
        patchGovQAState({
          isSendingFile: false,
        })
      );

      return;
    }

    let sessionId: string | undefined;
    // payload ||= { username, password }; // eslint-disable-line no-param-reassign
    const auth_payload = {
      username: payload?.username || username,
      password: payload?.password || password,
    };
    if (auth_payload) {
      const { username: username_, password: password_ } = auth_payload;

      let authResponse;
      try {
        authResponse = yield* call(govQAAPI.auth, {
          login: username_,
          password: password_,
          baseUrl,
          authKey,
          activationKey,
        });
      } catch (errMsg) {
        yield* put(
          patchGovQAState({
            authErrorMessage: isString(errMsg)
              ? errMsg
              : intl.formatMessage({
                  id: 'errorUnknown',
                  defaultMessage: 'Unknown error',
                }),
            isFormShown: true,
          })
        );

        yield* put(
          patchGovQAState({
            isSendingFile: false,
          })
        );

        return;
      }

      sessionId = authResponse.current_session_id;

      yield* put(setGovQASessionId(sessionId));
    } else {
      sessionId = yield* select(selectGovQASessionId);

      if (!sessionId) {
        console.error(
          'sendRedactedFileToGovQa: sessionId must be set in the store if auth payload is not passed to saga'
        );

        yield* put(
          patchGovQAState({
            isSendingFile: false,
          })
        );

        return;
      }
    }

    tdo = yield* select(selectTdo);

    if (!tdo) {
      yield* put(
        patchGovQAState({
          isSendingFile: false,
        })
      );

      return;
    }

    const { govQARequestId: uniqueId } = tdo.details;

    if (!uniqueId) {
      console.error(
        'sendRedactedFileToGovQa: govQARequestId must be set in TDO details'
      );

      yield* put(
        patchGovQAState({
          isSendingFile: false,
        })
      );

      return;
    }

    const mediaFile = getLastRedactedFile(tdo);
    // add this if statement to make compiler happy, but actually if
    // this saga fires fileUrl must be set
    if (!mediaFile?.signedUri) {
      console.error('sendRedactedFileToGovQa: signedUri is not present on TDO');

      yield* put(
        patchGovQAState({
          isSendingFile: false,
        })
      );

      return;
    }

    try {
      yield* call(govQAAPI.sendFile, {
        sessionId: sessionId,
        uniqueId: uniqueId,
        mediaFile: mediaFile,
        auditLog: auditLog,
        activationKey: activationKey,
        baseUrl: baseUrl,
      });
    } catch (_errMsg) {
      yield* put(setGovQASessionId(null));
      // NOTE: if used cached session id, need to pop up authentication dialog
      if (!payload) {
        yield* put(
          patchGovQAState({
            isFormShown: true,
          })
        );
      } else {
        yield* put(
          patchGovQAState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorSendingFile',
              defaultMessage: 'Error sending file',
            }),
          })
        );
      }

      yield* put(
        patchGovQAState({
          isSendingFile: false,
        })
      );

      return;
    }

    yield* put(
      patchGovQAState({
        successMessage: 'Successfully sent file to GovQA.',
        isStatusDialogShown: true,
        errorMessage: '',
      })
    );

    yield* put(
      patchGovQAState({
        isSendingFile: false,
      })
    );
  });
}

export interface FetchIntegrationInfoResponse {
  me: {
    organization: {
      id: string;
      integrationConfig?: {
        config: Record<string, string>;
      };
    };
  };
}

export function* sendRedactedFileToExternalIntegration() {
  const intl = sagaIntl();
  yield* takeEvery(
    SEND_REDACTED_FILE_TO_EXTERNAL_INTEGRATION,
    function* ({ payload: _payload }) {
      yield* put(
        patchExternalIntegrationState({
          isSendingFile: true,
        })
      );

      let tdo = yield* select(selectTdo);
      if (!tdo) {
        console.error('TDO should have existed');
        return;
      }
      yield* put(fetchLatestTDOAuditLogAsset(tdo.id));

      const { fetchAuditLogAssetResponse, errFetchingAuditLogAsset } =
        yield* race({
          fetchAuditLogAssetResponse: take(
            FETCH_LATEST_TDO_AUDIT_LOG_ASSET_SUCCESS
          ),
          errFetchingAuditLogAsset: take(
            FETCH_LATEST_TDO_AUDIT_LOG_ASSET_FAILURE
          ),
        });

      if (errFetchingAuditLogAsset) {
        yield* put(
          patchExternalIntegrationState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorFetchingAuditLog',
              defaultMessage: 'Error fetching audit log',
            }),
          })
        );

        yield* put(
          patchExternalIntegrationState({
            isSendingFile: false,
          })
        );

        return;
      }

      const auditLog =
        fetchAuditLogAssetResponse?.payload?.temporalDataObject?.assets
          ?.records?.[0];

      // make sure latest tdo is fetched before sending
      yield* put(fetchTdo(tdo.id));

      const { errFetchingTDO } = yield* race({
        _: take(FETCH_TDO_SUCCESS),
        errFetchingTDO: take(FETCH_TDO_WITH_FAILURE),
      });

      if (errFetchingTDO) {
        yield* put(
          patchExternalIntegrationState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorFetchingTDOState',
              defaultMessage: 'Error fetching TDO state',
            }),
          })
        );

        yield* put(
          patchExternalIntegrationState({
            isSendingFile: false,
          })
        );

        return;
      }

      yield* put(externalIntegrationAPI.fetchIntegrationInfo());

      const { ok, err } = yield* race({
        ok: take(externalIntegrationAPI.FETCH_INTEGRATION_INFO_SUCCESS),
        err: take(externalIntegrationAPI.FETCH_INTEGRATION_INFO_FAILURE),
      });

      if (err) {
        yield* put(
          patchExternalIntegrationState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorFetchingExternalIntegrationInfo',
              defaultMessage: 'Error fetching external integration info',
            }),
          })
        );

        yield* put(
          patchExternalIntegrationState({
            isSendingFile: false,
          })
        );

        return;
      }

      const integrationConfig =
        ok?.payload?.me?.organization?.integrationConfig?.config;

      if (!integrationConfig) {
        yield* put(
          patchExternalIntegrationState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'missingExternalIntegrationConfig',
              defaultMessage: 'Missing external integration config',
            }),
          })
        );

        yield* put(
          patchExternalIntegrationState({
            isSendingFile: false,
          })
        );

        return;
      }

      tdo = yield* select(selectTdo);

      if (!tdo) {
        yield* put(
          patchExternalIntegrationState({
            isSendingFile: false,
          })
        );

        return;
      }

      const mediaFile = getLastRedactedFile(tdo);
      if (!mediaFile?.signedUri) {
        console.error(
          'sendRedactedFileToExternalIntegration: signedUri is not present on TDO'
        );

        yield* put(
          patchExternalIntegrationState({
            isSendingFile: false,
          })
        );

        return;
      }

      const tdoId = tdo.id;
      const tdoName = tdo.name;

      // Folder (case) context is OPTIONAL for the send. The full case-media
      // payload is no longer eagerly loaded at TDO open (the redact-app entry
      // only triggers fetchCaseMedia when the drawer is opened), so we read the
      // case identity from the route's `caseId` and fetch its name on demand.
      // But a TDO can be opened without a folder in the route — "(Unassigned
      // Media)" or the classic view — in which case there is no caseId. The
      // integration templates treat `{{{folderId}}}` / `{{{folderName}}}` as
      // optional (replaced with '' when absent), so in that case we send
      // without folder context rather than failing the export (VE-23386).
      const currentRoutePayload = yield* select(selectCurrentRoutePayload);
      const caseId = currentRoutePayload?.caseId as CaseId | undefined;

      let folderId = '';
      let folderName = '';

      if (caseId) {
        yield* put(fetchFolderInfoService({ folderId: caseId }));
        const { folderInfoOk, folderInfoErr } = yield* race({
          folderInfoOk: take(FETCH_FOLDER_INFO_SUCCESS),
          folderInfoErr: take(FETCH_FOLDER_INFO_FAILURE),
        });

        if (folderInfoErr || !folderInfoOk) {
          console.error('Failed to fetch folder info for external integration');
          yield* put(
            patchExternalIntegrationState({
              isSendingFile: false,
            })
          );
          return;
        }

        folderId = folderInfoOk.payload?.folder?.id ?? '';
        folderName = folderInfoOk.payload?.folder?.name ?? '';
      }

      const reviewStatus = yield* select(selectOriginalFileStatus);
      if (!reviewStatus) {
        console.error('Review status should have existed');

        yield* put(
          patchExternalIntegrationState({
            isSendingFile: false,
          })
        );

        return;
      }

      const redactedWords = yield* select(selectWordsRedactedMap);
      if (!redactedWords) {
        console.error('Redacted words should have existed');

        yield* put(
          patchExternalIntegrationState({
            isSendingFile: false,
          })
        );

        return;
      }

      const redactedTranscript = yield* select(selectTranscriptAssets);

      try {
        yield* call(externalIntegrationAPI.sendFile, {
          tdoId: tdoId,
          tdoName: tdoName,
          folderId: folderId,
          folderName: folderName,
          reviewStatus: reviewStatus,
          transcriptionURL: redactedTranscript?.records[0]?.signedUri || '',
          mediaFile: mediaFile,
          auditLog: auditLog,
          integrationConfig: integrationConfig,
        });
      } catch (_errMsg) {
        yield* put(
          patchExternalIntegrationState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorSendingFile',
              defaultMessage: 'Error sending file',
            }),
          })
        );

        yield* put(
          patchExternalIntegrationState({
            isSendingFile: false,
          })
        );

        return;
      }

      yield* put(
        patchExternalIntegrationState({
          successMessage: 'Successfully sent file to External Integration.',
          isStatusDialogShown: true,
          errorMessage: '',
        })
      );

      yield* put(
        patchExternalIntegrationState({
          isSendingFile: false,
        })
      );
    }
  );
}

export function* sendRedactedFileToFOIAXpress() {
  const intl = sagaIntl();
  yield* takeEvery(
    SEND_REDACTED_FILE_TO_FOIAXPRESS,
    function* ({ payload: _payload }) {
      yield* put(
        patchFOIAXpressState({
          isSendingFile: true,
        })
      );

      let tdo = yield* select(selectTdo);
      if (!tdo) {
        console.error('TDO should have existed');
        return;
      }
      yield* put(fetchLatestTDOAuditLogAsset(tdo.id));

      const { fetchAuditLogAssetResponse, errFetchingAuditLogAsset } =
        yield* race({
          fetchAuditLogAssetResponse: take(
            FETCH_LATEST_TDO_AUDIT_LOG_ASSET_SUCCESS
          ),
          errFetchingAuditLogAsset: take(
            FETCH_LATEST_TDO_AUDIT_LOG_ASSET_FAILURE
          ),
        });

      if (errFetchingAuditLogAsset) {
        yield* put(
          patchFOIAXpressState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorFetchingAuditLog',
              defaultMessage: 'Error fetching audit log',
            }),
          })
        );

        yield* put(
          patchFOIAXpressState({
            isSendingFile: false,
          })
        );

        return;
      }

      const auditLog =
        fetchAuditLogAssetResponse?.payload?.temporalDataObject?.assets
          ?.records?.[0];

      // make sure latest tdo is fetched before sending
      yield* put(fetchTdo(tdo.id));

      const { errFetchingTDO } = yield* race({
        _: take(FETCH_TDO_SUCCESS),
        errFetchingTDO: take(FETCH_TDO_WITH_FAILURE),
      });

      if (errFetchingTDO) {
        yield* put(
          patchFOIAXpressState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorFetchingTDOState',
              defaultMessage: 'Error fetching TDO state',
            }),
          })
        );

        yield* put(
          patchFOIAXpressState({
            isSendingFile: false,
          })
        );

        return;
      }

      yield* put(foiaXpressAPI.fetchIntegrationInfo());

      const { ok, err } = yield* race({
        ok: take(foiaXpressAPI.FETCH_INTEGRATION_INFO_SUCCESS),
        err: take(foiaXpressAPI.FETCH_INTEGRATION_INFO_FAILURE),
      });

      if (err) {
        yield* put(
          patchFOIAXpressState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorFetchingFOIAXpressIntegrationInfo',
              defaultMessage: 'Error fetching FOIAXpress integration info',
            }),
          })
        );

        yield* put(
          patchFOIAXpressState({
            isSendingFile: false,
          })
        );

        return;
      }

      const { BASEURL: baseUrl } =
        ok?.payload?.me?.organization?.integrationConfig?.config || {};

      if (!baseUrl) {
        yield* put(
          patchFOIAXpressState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'missingFOIAXpressIntegrationConfig',
              defaultMessage: 'Missing FOIAXpress integration config',
            }),
          })
        );

        yield* put(
          patchFOIAXpressState({
            isSendingFile: false,
          })
        );

        return;
      }

      tdo = yield* select(selectTdo);

      if (!tdo) {
        yield* put(
          patchFOIAXpressState({
            isSendingFile: false,
          })
        );

        return;
      }

      const { foiaXpressRequestId: uniqueId } = tdo.details;

      if (!uniqueId) {
        console.error(
          'sendRedactedFileToFOIAXpress: foiaXpressRequestId must be set in TDO details'
        );

        yield* put(
          patchFOIAXpressState({
            isSendingFile: false,
          })
        );

        return;
      }

      const mediaFile = getLastRedactedFile(tdo);
      // add this if statement to make compiler happy, but actually if
      // this saga fires fileUrl must be set
      if (!mediaFile?.signedUri) {
        console.error(
          'sendRedactedFileToFOIAXpress: signedUri is not present on TDO'
        );

        yield* put(
          patchFOIAXpressState({
            isSendingFile: false,
          })
        );

        return;
      }

      try {
        yield* call(foiaXpressAPI.sendFile, {
          // sessionId,
          uniqueId,
          mediaFile,
          auditLog,
          // activationKey,
          baseUrl,
        });
      } catch (_errMsg) {
        // yield put(setFOIAXpressSessionId(null));
        // NOTE: if used cached session id, need to pop up authentication dialog
        // if (!payload) {
        //   yield put(
        //     patchFOIAXpressState({
        //       isFormShown: true,
        //     })
        //   );
        // } else {
        yield* put(
          patchFOIAXpressState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorSendingFile',
              defaultMessage: 'Error sending file',
            }),
          })
        );
        // }

        yield* put(
          patchFOIAXpressState({
            isSendingFile: false,
          })
        );

        return;
      }

      yield* put(
        patchFOIAXpressState({
          successMessage: 'Successfully sent file to FOIAXpress.',
          isStatusDialogShown: true,
          errorMessage: '',
        })
      );

      yield* put(
        patchFOIAXpressState({
          isSendingFile: false,
        })
      );
    }
  );
}

export function* sendRedactedFileToCasepoint() {
  const intl = sagaIntl();
  yield* takeEvery(
    SEND_REDACTED_FILE_TO_CASEPOINT,
    function* ({ payload: _payload }) {
      yield* put(
        patchCasepointState({
          isSendingFile: true,
        })
      );

      let tdo = yield* select(selectTdo);
      if (!tdo) {
        console.error('TDO should have existed');
        return;
      }
      yield* put(fetchLatestTDOAuditLogAsset(tdo.id));

      const { fetchAuditLogAssetResponse, errFetchingAuditLogAsset } =
        yield* race({
          fetchAuditLogAssetResponse: take(
            FETCH_LATEST_TDO_AUDIT_LOG_ASSET_SUCCESS
          ),
          errFetchingAuditLogAsset: take(
            FETCH_LATEST_TDO_AUDIT_LOG_ASSET_FAILURE
          ),
        });

      if (errFetchingAuditLogAsset) {
        yield* put(
          patchCasepointState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorFetchingAuditLog',
              defaultMessage: 'Error fetching audit log',
            }),
          })
        );

        yield* put(
          patchCasepointState({
            isSendingFile: false,
          })
        );

        return;
      }

      const auditLog =
        fetchAuditLogAssetResponse?.payload?.temporalDataObject?.assets
          ?.records?.[0];

      // make sure latest tdo is fetch before sending
      yield* put(fetchTdo(tdo.id));

      const { errFetchingTDO } = yield* race({
        _: take(FETCH_TDO_SUCCESS),
        errFetchingTDO: take(FETCH_TDO_WITH_FAILURE),
      });

      if (errFetchingTDO) {
        yield* put(
          patchCasepointState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorFetchingTDOState',
              defaultMessage: 'Error fetching TDO state',
            }),
          })
        );

        yield* put(
          patchCasepointState({
            isSendingFile: false,
          })
        );

        return;
      }

      yield* put(casepointAPI.fetchIntegrationInfo());

      const { ok, err } = yield* race({
        ok: take(casepointAPI.FETCH_INTEGRATION_INFO_SUCCESS),
        err: take(casepointAPI.FETCH_INTEGRATION_INFO_FAILURE),
      });

      if (err) {
        yield* put(
          patchCasepointState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorFetchingCasepointIntegrationInfo',
              defaultMessage: 'Error fetching Casepoint integration info',
            }),
          })
        );

        yield* put(
          patchCasepointState({
            isSendingFile: false,
          })
        );

        return;
      }

      const { BASEURL: baseUrl } =
        ok?.payload?.me?.organization?.integrationConfig?.config || {};

      if (!baseUrl) {
        yield* put(
          patchCasepointState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'missingCasepointIntegrationConfig',
              defaultMessage: 'Missing Casepoint integration config',
            }),
          })
        );

        yield* put(
          patchCasepointState({
            isSendingFile: false,
          })
        );

        return;
      }

      tdo = yield* select(selectTdo);

      if (!tdo) {
        yield* put(
          patchCasepointState({
            isSendingFile: false,
          })
        );

        return;
      }

      const { casepointRequestId: uniqueId } = tdo.details;

      if (!uniqueId) {
        console.error(
          'sendRedactedFileToCasepoint: casepointRequestId must be set in TDO details'
        );

        yield* put(
          patchCasepointState({
            isSendingFile: false,
          })
        );

        return;
      }

      const mediaFile = getLastRedactedFile(tdo);
      // add this if statement to make compiler happy, but actually if
      // this saga fires fileUrl must be set
      if (!mediaFile?.signedUri) {
        console.error(
          'sendRedactedFileToCasepoint: signedUri is not present on TDO'
        );

        yield* put(
          patchCasepointState({
            isSendingFile: false,
          })
        );

        return;
      }

      try {
        yield* call(casepointAPI.sendFile, {
          // sessionId,
          uniqueId,
          mediaFile,
          auditLog,
          // activationKey,
          baseUrl,
        });
      } catch (_errMsg) {
        yield* put(
          patchCasepointState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorSendingFile',
              defaultMessage: 'Error sending file',
            }),
          })
        );
        // }

        yield* put(
          patchCasepointState({
            isSendingFile: false,
          })
        );

        return;
      }

      yield* put(
        patchCasepointState({
          successMessage: 'Successfully sent file to Casepoint.',
          isStatusDialogShown: true,
          errorMessage: '',
        })
      );

      yield* put(
        patchCasepointState({
          isSendingFile: false,
        })
      );
    }
  );
}

export function* sendRedactedFileToExterro() {
  const intl = sagaIntl();
  yield* takeEvery(
    SEND_REDACTED_FILE_TO_EXTERRO,
    function* ({ payload: _payload }) {
      yield* put(
        patchExterroState({
          isSendingFile: true,
        })
      );

      let tdo = yield* select(selectTdo);
      if (!tdo) {
        console.error('TDO should have existed');
        return;
      }
      yield* put(fetchLatestTDOAuditLogAsset(tdo.id));

      const { fetchAuditLogAssetResponse, errFetchingAuditLogAsset } =
        yield* race({
          fetchAuditLogAssetResponse: take(
            FETCH_LATEST_TDO_AUDIT_LOG_ASSET_SUCCESS
          ),
          errFetchingAuditLogAsset: take(
            FETCH_LATEST_TDO_AUDIT_LOG_ASSET_FAILURE
          ),
        });

      if (errFetchingAuditLogAsset) {
        yield* put(
          patchExterroState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorFetchingAuditLog',
              defaultMessage: 'Error fetching audit log',
            }),
          })
        );

        yield* put(
          patchExterroState({
            isSendingFile: false,
          })
        );

        return;
      }

      const auditLog =
        fetchAuditLogAssetResponse?.payload?.temporalDataObject?.assets
          ?.records?.[0];

      // make sure latest tdo is fetch before sending
      yield* put(fetchTdo(tdo.id));

      const { errFetchingTDO } = yield* race({
        _: take(FETCH_TDO_SUCCESS),
        errFetchingTDO: take(FETCH_TDO_WITH_FAILURE),
      });

      if (errFetchingTDO) {
        yield* put(
          patchExterroState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorFetchingTDOState',
              defaultMessage: 'Error fetching TDO state',
            }),
          })
        );

        yield* put(
          patchExterroState({
            isSendingFile: false,
          })
        );

        return;
      }

      yield* put(exterroAPI.fetchIntegrationInfo());

      const { ok, err } = yield* race({
        ok: take(exterroAPI.FETCH_INTEGRATION_INFO_SUCCESS),
        err: take(exterroAPI.FETCH_INTEGRATION_INFO_FAILURE),
      });

      if (err) {
        yield* put(
          patchExterroState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorFetchingExterroIntegrationInfo',
              defaultMessage: 'Error fetching Exterro integration info',
            }),
          })
        );

        yield* put(
          patchExterroState({
            isSendingFile: false,
          })
        );

        return;
      }

      const { BASEURL: baseUrl, SIGNATURE: signature } =
        ok?.payload?.me?.organization?.integrationConfig?.config || {};

      if (!baseUrl || !signature) {
        yield* put(
          patchExterroState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'missingExterroIntegrationConfig',
              defaultMessage: 'Missing Exterro integration config',
            }),
          })
        );

        yield* put(
          patchExterroState({
            isSendingFile: false,
          })
        );

        return;
      }

      tdo = yield* select(selectTdo);

      if (!tdo) {
        yield* put(
          patchExterroState({
            isSendingFile: false,
          })
        );

        return;
      }

      const { exterroRequestId: uniqueId } = tdo.details;

      if (!uniqueId) {
        console.error(
          'sendRedactedFileToExterro: exterroRequestId must be set in TDO details'
        );

        yield* put(
          patchExterroState({
            isSendingFile: false,
          })
        );

        return;
      }

      const mediaFile = getLastRedactedFile(tdo);
      // add this if statement to make compiler happy, but actually if
      // this saga fires fileUrl must be set
      if (!mediaFile?.signedUri) {
        console.error(
          'sendRedactedFileToExterro: signedUri is not present on TDO'
        );

        yield* put(
          patchExterroState({
            isSendingFile: false,
          })
        );

        return;
      }

      try {
        yield* call(exterroAPI.sendFile, {
          // sessionId,
          uniqueId,
          mediaFile,
          auditLog,
          // activationKey,
          signature,
          baseUrl,
        });
      } catch (_errMsg) {
        yield* put(
          patchExterroState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorSendingFile',
              defaultMessage: 'Error sending file',
            }),
          })
        );
        // }

        yield* put(
          patchExterroState({
            isSendingFile: false,
          })
        );

        return;
      }

      yield* put(
        patchExterroState({
          successMessage: 'Successfully sent file to Exterro.',
          isStatusDialogShown: true,
          errorMessage: '',
        })
      );

      yield* put(
        patchExterroState({
          isSendingFile: false,
        })
      );
    }
  );
}

export function* sendRedactedFileToNuix() {
  const intl = sagaIntl();
  yield* takeEvery(
    SEND_REDACTED_FILE_TO_NUIX,
    function* ({ payload: _payload }) {
      yield* put(
        patchNuixState({
          isSendingFile: true,
        })
      );

      let tdo = yield* select(selectTdo);
      if (!tdo) {
        console.error('TDO should have existed');
        return;
      }
      // yield* put(fetchLatestTDOAuditLogAsset(tdo.id));

      // const { fetchAuditLogAssetResponse, errFetchingAuditLogAsset } =
      //   yield* race({
      //     fetchAuditLogAssetResponse: take(FETCH_LATEST_TDO_AUDIT_LOG_ASSET_SUCCESS),
      //     errFetchingAuditLogAsset: take(
      //       FETCH_LATEST_TDO_AUDIT_LOG_ASSET_FAILURE
      //     ),
      //   });

      // if (errFetchingAuditLogAsset) {
      //   yield* put(
      //     patchNuixState({
      //       successMessage: '',
      //       isStatusDialogShown: true,
      //       errorMessage:
      //         'Unexpected error fetching audit logs. Please try again.',
      //     })
      //   );

      //   yield* put(
      //     patchNuixState({
      //       isSendingFile: false,
      //     })
      //   );

      //   return;
      // }

      // const auditLog =
      //   fetchAuditLogAssetResponse?.payload?.temporalDataObject?.assets
      //     ?.records?.[0];

      // make sure latest tdo is fetch before sending
      yield* put(fetchTdo(tdo.id));

      const { errFetchingTDO } = yield* race({
        _: take(FETCH_TDO_SUCCESS),
        errFetchingTDO: take(FETCH_TDO_WITH_FAILURE),
      });

      if (errFetchingTDO) {
        yield* put(
          patchNuixState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorFetchingTDOState',
              defaultMessage: 'Error fetching TDO state',
            }),
          })
        );

        yield* put(
          patchNuixState({
            isSendingFile: false,
          })
        );

        return;
      }

      yield* put(nuixAPI.fetchIntegrationInfo());

      const { ok, err } = yield* race({
        ok: take(nuixAPI.FETCH_INTEGRATION_INFO_SUCCESS),
        err: take(nuixAPI.FETCH_INTEGRATION_INFO_FAILURE),
      });

      if (err) {
        yield* put(
          patchNuixState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorFetchingNuixIntegrationInfo',
              defaultMessage: 'Error fetching Nuix integration info',
            }),
          })
        );

        yield* put(
          patchNuixState({
            isSendingFile: false,
          })
        );

        return;
      }

      const { BASEURL: baseUrl, SIGNATURE: signature } =
        ok?.payload?.me?.organization?.integrationConfig?.config || {};

      if (!signature) {
        yield* put(
          patchNuixState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'missingNuixIntegrationConfig',
              defaultMessage: 'Missing Nuix integration config',
            }),
          })
        );

        yield* put(
          patchNuixState({
            isSendingFile: false,
          })
        );

        return;
      }

      tdo = yield* select(selectTdo);

      if (!tdo) {
        yield* put(
          patchNuixState({
            isSendingFile: false,
          })
        );

        return;
      }

      const { nuixRequestId: uniqueId } = tdo.details;

      if (!uniqueId) {
        console.error(
          'sendRedactedFileToNuix: nuixRequestId must be set in TDO details'
        );

        yield* put(
          patchNuixState({
            isSendingFile: false,
          })
        );

        return;
      }

      const mediaFile = getLastRedactedFile(tdo);
      // add this if statement to make compiler happy, but actually if
      // this saga fires fileUrl must be set
      if (!mediaFile?.signedUri) {
        console.error(
          'sendRedactedFileToNuix: signedUri is not present on TDO'
        );

        yield* put(
          patchNuixState({
            isSendingFile: false,
          })
        );

        return;
      }

      const url =
        baseUrl ||
        'https://ringtaildr.us.nuix.com/ringtail-svc-portal/api/query';
      try {
        yield* call(nuixAPI.sendFile, {
          uniqueId,
          mediaFile,
          signature,
          baseUrl: url,
        });
      } catch (_errMsg) {
        yield* put(
          patchNuixState({
            successMessage: '',
            isStatusDialogShown: true,
            errorMessage: intl.formatMessage({
              id: 'errorSendingFile',
              defaultMessage: 'Error sending file',
            }),
          })
        );
        // }

        yield* put(
          patchNuixState({
            isSendingFile: false,
          })
        );

        return;
      }

      yield* put(
        patchNuixState({
          successMessage: 'Successfully sent file to Nuix.',
          isStatusDialogShown: true,
          errorMessage: '',
        })
      );

      yield* put(
        patchNuixState({
          isSendingFile: false,
        })
      );
    }
  );
}

export function* watchOverlaySelectionChanges() {
  yield* takeEvery([UDR_SELECTED_FROM_TIMELINE, FACE_HIGHLIGHT], function* () {
    const selectedUDRGroupId = yield* select(selectSelectedUDRGroupId);

    // NOTE: saga fires after reducers so selectedUDRGroupId should be already updated
    if (!selectedUDRGroupId) {
      yield* put(setSelectedUdrGroupOnWebWorkerOnly());
    }
  });
}

export function* onCleanDeletedOverlaysCache() {
  yield* debounce(
    // NOTE: should have something to remove after this interval
    DELETE_OVERLAY_CACHE_INTERVAL + 5000,
    [
      DELETE_DETECTION_OVERLAY,
      DELETE_DETECTION_IN_FRAME,
      DELETE_DETECTION_SEGMENT,
      DELETE_DETECTION_GROUP,
      DELETE_UDR_OVERLAY,
      DELETE_UDR_IN_FRAME,
      DELETE_UDR_SEGMENT,
      DELETE_UDR_GROUP,
    ],
    function* () {
      let deletedOverlaysCache = yield* select(selectDeletedOverlaysCache);

      deletedOverlaysCache = cleanDeleteOverlayCache(deletedOverlaysCache);

      yield* put(actionSetDeletedOverlaysCache(deletedOverlaysCache));
    }
  );
}

export function* loadWaveformCsv() {
  const tdo = yield* select(selectTdo);

  const waveformAsset = tdo?.waveformAssets?.records?.[0];
  if (waveformAsset) {
    const { signedUri } = waveformAsset;
    yield* put(fetchWaveformCsv(signedUri));
  }
}

export function* watchForWaveformSuccess() {
  yield* takeEvery(FETCH_WAVEFORM_SUCCESS, function* ({ payload: waveforms }) {
    yield* put(setAudiowaves(waveforms));
  });
}

export function* watchForWaveformFailure() {
  yield* takeEvery(FETCH_WAVEFORM_FAILURE, function* () {});
}

function* watchSendToReloadResults() {
  yield* takeEvery(LOAD_ENGINE_RESULTS_AFTER_DETECT, function* () {
    const tdoId = yield* select(selectCurrentTdoId);
    const { detectionEngineIds, detectionCategory } =
      yield* select(selectConfigEngines);
    const mediaDuration = yield* select(selectMediaDuration);
    if (tdoId) {
      yield* put(
        IN_LOAD_DETECTION_DATA({
          tdoId,
          engines: detectionEngineIds,
          mediaDuration,
          categoryId: detectionCategory,
        })
      );
    }
  });
}

function* watchVideoHasLoaded() {
  yield* takeLatest(VIDEO_HAS_LOADED, function* () {
    // TODO: this is a workaround to set video react hasStarted to true
    // This is the replacement of player.hasStarted = true at line
    // https://github.com/veritone/redact-app/pull/2762/files#diff-0682378934ad17c6b2d725411dca9def66a798875f1f23b36d6b3c802e216795L89
    // We can not add dispatch(actionSetHasStartedPlayer(true)) to MediaPlayerView.tsx (infinite loop), or
    // add dispatch(actionSetHasStartedPlayer(true)) to useEffect (overwritten by action video-react/LOAD_START).
    // Alternative: remove hasStarted from redux store, and add videoReactHasStarted as property to those components need it, and
    // has videoReactHasStarted react state in MediaPlayerView.tsx and pass it to those components.
    yield* put(actionSetHasStartedPlayer(true));
  });
}

export function* watchRotateVideo() {
  yield* takeLatest(ROTATE_VIDEO, rotateVideoHandle);
}

export function* watchRotateVideoSuccess() {
  yield* takeLatest(CREATE_ROTATE_VIDEO_JOB_SUCCESS, function* () {
    const intl = sagaIntl();
    const isImage = Boolean(yield* call(isImageFile));

    const message = isImage
      ? intl.formatMessage({
          id: 'rotateImageSuccess',
          defaultMessage: 'Processing Image Rotation.',
        })
      : intl.formatMessage({
          id: 'rotateVideoSuccess',
          defaultMessage: 'Processing Video Rotation.',
        });

    yield* put(
      enqueueSnackbar({
        message,
        variant: 'success',
      })
    );

    yield* put(redirect(REDIRECT_TO_HOME()));
  });
}

export function* watchRotateVideoFailure() {
  yield* takeLatest(CREATE_ROTATE_VIDEO_JOB_FAILURE, function* () {
    const intl = sagaIntl();
    const isImage = Boolean(yield* call(isImageFile));

    const message = isImage
      ? intl.formatMessage({
          id: 'rotateImageFailure',
          defaultMessage: 'Unexpected error occurred while rotating image.',
        })
      : intl.formatMessage({
          id: 'rotateVideoFailure',
          defaultMessage: 'Unexpected error occurred while rotating video.',
        });

    yield* put(
      enqueueSnackbar({
        message,
        variant: 'error',
      })
    );

    yield* put(redirect(REDIRECT_TO_HOME()));
  });
}

export function* rotateVideoHandle({
  payload: rotationDegrees,
}: {
  payload: number;
}) {
  const tdo: DetailTDO | null = yield* select(selectTdo);

  if (tdo) {
    const detectObjects: {
      headObjects: boolean;
      personObjects: boolean;
    } = yield* select(selectDetectObjects);

    const { audioRedactionEdits, voiceMaskEdits } = yield* select(
      selectTranscriptionView
    );

    const backgroundBlurEdits = yield* select(selectBackgroundBlurEdits);

    // Remove existing UDRs
    yield* put(OUT_UDR_CLUSTER_GROUPS({}));
    yield* put(OUT_UDR_COLLECTION());
    yield* put(OUT_TEXT_OVERLAY_COLLECTION());
    yield* put(OUT_UDR_ASSET({ boundingPolys: {} }));

    // Remove existing detections
    yield* put(OUT_DETECTION_CLUSTER_GROUPS({}));
    yield* put(OUT_BOUNDING_POLY_COLLECTION({}));

    // Remove selected polys
    yield* put(OUT_SELECTED_BOUNDINGPOLY_GROUP({ selected: {} }));
    yield* put(actionSetSelectedGroups({ selected: {} }));

    // Remove clusters map
    yield* put(OUT_CLUSTER_MAP({}));

    // Discard changes because UDR/head detections is deleted
    // Make sure user can auto-exit TDO after successful rotate job creation
    yield* put(DISCARD_CHANGES());

    yield* put(
      saveMediaData({
        tdoId: tdo.id,
        selectedPolyGroups: {},
        boundingPolys: {},
        clusterMap: {},
        audioRedactions: audioRedactionEdits,
        voiceMaskEdits,
        backgroundBlurEdits,
      })
    );

    yield* put(deleteAllHeadDetectionResults({}));

    // update tdo status to recording
    yield* put(updateTDOStatus('recording'));

    yield* put(rotateVideo(tdo.id, rotationDegrees, detectObjects));
  }
}
